/**
 * execReportFactPack.js — build the Executive Report (AI) fact pack.
 *
 * WHY THIS LIVES CLIENT-SIDE. The report is derived from the same ~1,400 lines of
 * attacker-window and event-filtering logic the two existing reports use. Deriving
 * it again server-side would mean maintaining a second copy in PowerShell forever.
 * So the browser BUILDS the pack, and the server RECONCILES it: analyst judgments
 * (which IPs are malicious, what was written about them) are rebuilt from the case
 * entity and the client's version discarded; a defined subset is verified against
 * the pinned run blob; everything else is recorded with provenance. See
 * Plans/2026-08-07_Executive-Report-AI.md §8.
 *
 * WHAT THE MODEL SEES. Never a raw timestamp — every time is pre-formatted as a
 * display string in the case timezone, so the LLM never does timezone math. Never
 * a fact it can restate wrongly in a figure slot either: metrics and timeline rows
 * are keyed by IDs, and the SERVER renders the values.
 *
 * Deliberately imports only PURE modules (eventUtils, attackerWindow) — no JSX —
 * so scripts/execReportFactPack.test.mjs can run it under bare node.
 */

import {
  combineAndTagEvents,
  buildIpLookup,
  parseFolders,
  getMailAccessType,
  maskSubject,
} from './eventUtils.js';
import { collapseMessageTraceInStream } from './messageTraceCollapse.js';
import { applyAllEventsRollups } from './allEventsRollups.js';
import { REPORT_FILTER_PROFILE, CANONICAL_FILTERS } from './execReportFilterProfile.js';
import {
  buildAttackerContextFrom, buildAttackerDeviceIndex, attackerBasis, attackerBasisCapable,
  parseAttackerEventMarks, analystMarkOf,
} from './attackerAttribution.js';
import {
  isDeviceOp, isDeviceRegistrationOp, dirAuditDeviceName, dirAuditDeviceObjectId, eventDeviceNames,
  eventDeviceIds, eventDeviceLabel,
} from './deviceRegistration.js';
import {
  flaggedPhishMessageIndex, collectReleaseEvidence, maliciousActivityBounds,
  selectRelatedMessages, relatedMessageSilverId, relatedPhishingRows, RELATED_SCOPE_PAD,
  relatedIsSameMessage, relatedRecipientClicks,
} from './quarantineRelease.js';
import { buildExposures, isUrlShapedApp } from './attackerAccess.js';
import { refusedAttemptKeys, isTokenIssuingSignIn } from './refusedAttempts.js';

/** Must match Get-ExecReportFactSchemaVersion in api/Shared/ExecutiveReportHelper.psm1. */
// 1.1 — `accessed_items` replaces `mailbox_folders[].subjects`.
// 1.2 — `phishing_entry_point` replaces `url_clicks_confirmed` (which carried an eventId
//       and nothing else, so no finding could be written from it).
// 1.3 — `attacker_devices`: machines the attacker joined to the tenant. Persistence a
//       password reset does not clear, and absent from every report before v1.158.0.
// 1.4 — `accessed_items` gains the `Deleted` action and a `detail` field. Mail deletions
//       were previously emitted as `Read` (see mailItemAction), so the vocabulary WIDENS:
//       a server on 1.3 rejects a `Deleted` action outright, which is why this is a
//       version bump and not a quiet addition.
// 1.17 — `phishing_entry_point` gains the quarantine release (attached to the EMAIL it
//        describes) and the "other accounts to review" lead: `related_messages`,
//        `related_message_ids`, `related_scope` and its display bounds. The renderers
//        print a server-recomputed recipient total from the ids, so a 1.16 server would
//        drop the whole finding silently rather than render it — hence a version bump.
// 1.23 — the pipeline team's levig accuracy review: `attacker_window` gains `noTokenIssued`,
//        `attemptCount`, `requestedApps` and `accountState` (re-enables included);
//        top-level `exposures`; `phishing_entry_point.clicks[].fromEmailEventId`;
//        `malicious_ips[].failedSignInCount`; URL-shaped "applications" leave `apps` for
//        `phishing_pages`. Every wording change they drive is server-rendered.
// 1.24 — `related_messages[]` gains `sameMessage` (exact NetworkMessageId / InternetMessageId
//        match with the flagged email), `clickCount`, `firstClickDisplay` and
//        `clickedConfirmedLink` (that recipient's own clicks in that message). Server re-derives
//        all but the display time from the pinned blob.
// 1.25 — the pipeline team's ehribik review (password captured, MFA blocked every attempt):
//        `attacker_window` gains `passwordAccepted`, `mfaChallenged`, `mfaChallengePush` and
//        `mfaCompleted`, and its first/last displays span every refused ATTEMPT when no token
//        was issued; `malicious_ips[].signInAttemptCount`. Server re-derives each from the blob.
// 1.26 — the Report/Brief parity round (2026-10-28): the mail figures (`mailbox_items_accessed`,
//        `distinct_messages_read`, `mailbox_folders_touched`, `inbox_rules_created`,
//        `apps_consented`, `emails_sent`) and `mailbox_folders` count attacker-attributed
//        activity only, with labels that say so. No field added or removed.
// The server compares this EXACTLY and tells the analyst to reload, so a browser tab left
// open across the deploy fails with an instruction rather than half a report.
export const FACT_SCHEMA_VERSION = '1.27';

/**
 * The blob arrays that count as RECORDS -- the ones the report's figures are drawn from.
 *
 * 🔴 MIRRORS `$script:ExecReportRecordArrays` in api/Shared/ExecutiveReportHelper.psm1, and a
 * test reads the PowerShell and compares. Same discipline as ACCESSED_ITEM_ACTIONS and
 * ATTACKER_ACTION_IDS: two sides must not hold independent opinions about what a record is,
 * because "Records in run" is printed next to "N incident related; M normal activity" and the
 * reader adds them up.
 *
 * These are exactly the arrays `combineAndTagEvents` walks. Deliberately absent:
 * `ipAddresses` and `enterpriseApps` (per-address / per-app summaries, not events),
 * `secureScoreEvents` (a tenant metric), `dataSourceErrors` (diagnostics), and
 * `relatedPhishingMessages` -- real events, excluded by decision (operator, 2026-08-25)
 * because they are not in the timeline these figures come from and have their own surface.
 */
export const RECORD_ARRAYS = [
  'signInEvents', 'nonInteractiveSignInEvents', 'exchangeEvents', 'sharePointEvents',
  'directoryAuditEvents', 'messageTraceEvents', 'urlClickEvents', 'riskDetectionEvents',
  'riskyUserEvents', 'dlpEvents', 'generalEvents', 'azureActivityEvents',
  'securityAlertEvents', 'securityIncidentEvents',
];

/**
 * The same fourteen workloads as RECORD_ARRAYS, spelled the way `normalizeResults` delivers
 * them on the `results` object. The denominator of the scope sentence.
 *
 * 🔴 TWO SPELLINGS OF ONE LIST, so a test asserts they name the same workloads. `results` is
 * PascalCase and the run blob is camelCase, and "Records in run" is printed next to
 * "N incident related; M normal activity" -- a workload present in one list and absent from
 * the other would make those two figures disagree by exactly its size, which is the v1.166.1
 * defect (439 records) in a new place.
 */
export const RESULT_ARRAYS = [
  'SignInEvents', 'NonInteractiveSignInEvents', 'ExchangeEvents', 'SharePointEvents',
  'DirectoryAuditEvents', 'MessageTraceEvents', 'UrlClickEvents', 'RiskDetectionEvents',
  'RiskyUserEvents', 'DlpEvents', 'GeneralEvents', 'AzureActivityEvents',
  'SecurityAlertEvents', 'SecurityIncidentEvents',
];

/**
 * The closed metric vocabulary. The SERVER owns this list (Get-ExecReportMetricIds);
 * this copy exists so the builder can only emit ids the server will accept — an
 * unknown id is a hard validation failure there, not a silent drop.
 */
export const METRIC_IDS = [
  'attacker_window_duration',
  'attacker_signins',
  'successful_attacker_signins',
  // 🔴 A SEPARATE metric, never folded into attacker_signins (v1.172.0). An interactive
  // sign-in means the attacker authenticated; a non-interactive authentication means a
  // token was USED, possibly replayed. Merging them would assert N events of one kind and
  // erase the distinction the feed exists to surface -- and the ceiling would become
  // meaningless, since attacker_signins is bounded by the INTERACTIVE workload total.
  'attacker_token_use',
  'malicious_ip_count',
  'distinct_apps_used',
  'mailbox_items_accessed',
  'distinct_messages_read',
  'mailbox_folders_touched',
  'inbox_rules_created',
  'mfa_methods_changed',
  'apps_consented',
  'emails_sent',
  'message_trace_total',
  'files_accessed',
  'files_downloaded',
  'confirmed_phish_clicks',
  'attacker_devices_registered',
  'log_types_reviewed_count',
];

/** Caps mirror Test-ExecReportFactPack's, so a pack built here always validates there. */
export const CAPS = {
  // 🔴 ROWS PER SUBMISSION, NOT ROWS IN TOTAL - and 150 was neither (v1.176.0).
  //
  // 150 was written in the design doc on 2026-08-07, before anything had been measured,
  // above the only case that existed (the fixture case makes 85 rows). It was intended as
  // a runaway bound like `attackerDevices` below, it carried NO comment saying so, and it
  // quietly became an editorial cut. It bound on the REAL shipped Brief for
  // IR-20260818-Controller-001: timeline = 150 of 483 rows, verified from that report's own
  // facts blob in storage. Because the cut was CHRONOLOGICAL and containment is always LAST,
  // hitting it always removed the case's ending -- all four containment rows on that case sit
  // at indices 656-666 of 667, so the model wrote the bottom line having seen Jul 9 - Jul 29
  // of a window closing Aug 3, with no remediation row at all.
  //
  // ⚠️ NO RATE IS CLAIMED, and an earlier draft of this comment claimed one ("10 of 20
  // packs"). That figure was measured over the local tmp/facts_*.json collection, which is
  // mostly CEILING builds from execReportBuildRealPack.mjs -- it passes the unfiltered run as
  // both results and filteredResults, so every count is overstated. Separating them by
  // `excludedEventCount > 0` does NOT work either: the canonical filters exclude events in a
  // ceiling build too. Only a facts blob pulled from storage is evidence about a real report.
  //
  // The reason it could not simply be raised was a 20,000 tokens/minute deployment ceiling
  // that the v1.142.0 unmasked report already used 97.3% of. THAT CEILING WAS RAISED TO
  // 200,000 ON 2026-08-12 and nobody revisited the caps sized for it -- the raise even
  // reasoned from their existence ("the saturated worst case under the fact-pack caps").
  //
  // 1,500 is where the budget actually binds, not a guess:
  //     MaxInput 120,000 - MaxOutput 8,000 (Azure admits against prompt + max output)
  //     - the rest of the model payload (~2,050 tok) - system prompt + schema (~2,150 tok)
  //     = ~105,900 tok / ~72 tok per row  =>  ~1,476 rows.
  // Above that a request CANNOT succeed, so the bound converts an unwinnable token
  // preflight rejection -- after the job is queued, leased and charged -- into a countable
  // truncation at the request boundary.
  //
  // 🔴 AND A CAP THAT BINDS NO LONGER TRUNCATES SILENTLY. Two things changed with it:
  // `selectTimelineRows` picks by significance instead of taking a prefix (containment is
  // never droppable), and whatever it drops is counted into `omitted_count` -- which the
  // old `.slice()` did not do, making the server's own loud over-cap check unreachable.
  timeline: 1500,
  // Days in `timeline_digest`. A runaway bound only: the digest has one entry per calendar
  // day of ATTACKER-significant activity, and the case range the UI allows is bounded, so
  // ten entries is typical and this exists so a pathological range cannot run away.
  timelineDigestDays: 200,
  // 🔴 A RUNAWAY BOUND, NOT AN EDITORIAL ONE (v1.203.0). It was 50 and took the first 50 in
  // classification order, so on IR-20260625-waynecavanaugh-001 (111 classified) the server's
  // reconcile -- which rebuilds this list from the case's own classifications -- added the other
  // 61 back as bare rows and Addendum D printed them as dashes, among them the most active
  // addresses. The largest case (251) fits; rows are ordered by successful activity below.
  maliciousIps: 300,
  folders: 60,
  // The matrix renders EVERY item the analyst approved (decision 2026-08-12): it is
  // the exposure evidence the report exists to deliver, so a silent cut is worse than
  // a long table. This cap is a runaway bound, not an editorial one — the reference
  // case needs 371 rows, and anything dropped by it is counted into omitted_count and
  // stated in the report.
  accessedItems: 1500,
  inboxRules: 40,
  mfaChanges: 40,
  // Machines the attacker joined to the tenant. A runaway bound, not an editorial one:
  // six on the reference case, and an analyst who sees forty has a different incident.
  attackerDevices: 40,
  apps: 100,
  scopeNotes: 20,
  // The analyst's phishing entry point. Small by nature — an analyst marks the lure and
  // the click, not a corpus — so these are runaway bounds, and anything beyond them is
  // a sign the markers were used for something other than an entry point.
  phishEmails: 10,
  phishClicks: 25,
  // Other accounts the flagged sender also delivered to. An EDITORIAL cut, not a runaway
  // bound — an investigator reads the earliest 25 and follows the count — so truncation is
  // DISCLOSED ("showing 25 of N messages"), never silent. Chronological, so what is kept
  // is the start of the delivery sequence.
  relatedMessages: 25,
  // The full in-scope population's ids, so the server can recompute the printed totals
  // from rows it verified rather than trusting a client number. Ids are small; this is a
  // runaway bound on a list that is never rendered.
  relatedMessageIds: 500,
  bytes: 400 * 1024,
};

/**
 * Frontend `_eventType` tag -> canonical Databricks `log_type`.
 *
 * `log_types_reviewed` is checked server-side against Get-ExecReportRunWorkloadTotals,
 * which is keyed by the snake_case log_type vocabulary (signin, url_click, ...). The
 * pack used to emit raw `_eventType` tags instead, and the two only *appeared* to
 * agree: PowerShell hashtable lookups are case-insensitive, so single-word tags like
 * SignIn/Exchange matched by accident while every multi-word tag failed on the
 * underscore — a 422 on any case containing url_click, directory_audit, message_trace,
 * risk_detection, risky_user, azure_activity, security_alert or security_incident
 * events, which is nearly all of them.
 *
 * Two tags are not 1:1 workloads and are mapped deliberately:
 *   SharePointList — a SharePoint sub-category, not its own log_type.
 *   AlertEvidence  — synthesized from security_alert evidence (eventUtils.js:1279),
 *                    with no array of its own in the run blob.
 * Both collapse onto the log source they came from, which also makes
 * log_types_reviewed_count count distinct LOG SOURCES rather than distinct UI tags.
 *
 * A tag missing from this map is dropped rather than sent, because an unmapped tag
 * would 422 the whole report instead of costing one line of context.
 * scripts/execReportFactPack.test.mjs asserts every `_eventType` in eventUtils.js is
 * mapped here, so a new tag fails a test instead of failing silently in production.
 */
export const LOG_TYPE_BY_EVENT_TYPE = {
  SignIn: 'signin',
  Exchange: 'exchange',
  SharePoint: 'sharepoint',
  SharePointList: 'sharepoint',
  MessageTrace: 'message_trace',
  DirectoryAudit: 'directory_audit',
  UrlClick: 'url_click',
  RiskDetection: 'risk_detection',
  RiskyUser: 'risky_user',
  DLP: 'dlp',
  General: 'general',
  AzureActivity: 'azure_activity',
  SecurityAlert: 'security_alert',
  SecurityIncident: 'security_incident',
  AlertEvidence: 'security_alert',
  // The non-interactive feed. It reaches the pack through the RENDERED timeline only
  // (see timelineEvents below), never through `events` -- so it is listed as a reviewed
  // workload but contributes to no metric. The server must know the name:
  // Get-ExecReportRunWorkloadTotals maps it to the run blob's nonInteractiveSignInEvents
  // array, and anything not in that map is a reconcile failure.
  NonInteractiveSignIn: 'graph_signin_noninteractive',
};

const INBOX_RULE_OPS = new Set(['New-InboxRule', 'Set-InboxRule', 'UpdateInboxRules', 'Remove-InboxRule']);
const MFA_OPS = [
  'update user', 'user registered security info', 'user changed default security info',
  'user deleted security info', 'admin registered security info', 'reset user password',
  // v1.189.1: an administrator removing a method — on IR-20261001-ahayman-001 the client's
  // admin deleting the attacker's authenticator. Absent, the remediation never reached
  // "Mitigation steps taken" (the pipeline handoff's acceptance test expected it there).
  'admin deleted security info',
  'user started security info registration', 'user completed security info registration',
];
const CONSENT_OPS = [
  'consent to application', 'add app role assignment grant to user',
  'add delegated permission grant', 'add oauth2permissiongrant',
];
const REMEDIATION_OPS = [
  'disable account', 'reset user password', 'change user password',
  'revoke sign-in sessions', 'update stssessionvalidtimestamp', 'update user',
];

// ── Formatting ──────────────────────────────────────────────────────────────

/**
 * Format a UTC timestamp for display in the case's timezone. Every time in the
 * pack goes through here — the model receives display strings only.
 */
export function formatFactTime(iso, tzIana, { dateOnly = false, dayNoYear = false, timeOnly = false } = {}) {
  if (!iso) return '';
  const d = new Date(normalizeIso(iso));
  if (Number.isNaN(d.getTime())) return String(iso);
  // dayNoYear ("Jul 30") and timeOnly ("1:25 PM") split one instant into a day label and a
  // time so the Brief's containment column can group occurrences by day WITHOUT the server ever
  // parsing a display string (the project keeps all tz math on this side).
  const opts = dateOnly
    ? { year: 'numeric', month: 'short', day: 'numeric' }
    : dayNoYear
      ? { month: 'short', day: 'numeric' }
      : timeOnly
        ? { hour: 'numeric', minute: '2-digit' }
        : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  // 🔴 A BARE `YYYY-MM-DD` IS A CALENDAR DATE, NOT AN INSTANT, AND MUST NOT BE CONVERTED.
  // `new Date('2026-07-09')` is midnight UTC, so rendering it in EDT gives "Jul 8" -- which
  // is how the brief's header came to read "Jul 8 - Aug 17" for a case whose range is
  // 2026-07-09 to 2026-08-18, putting the Aug 18 remediation it lists outside its own stated
  // window and disagreeing with its own Provenance block.
  //
  // 🔴 THE TEST IS THE INPUT'S SHAPE, NEVER THE `dateOnly` FLAG. `generatedAtDisplay` passes
  // a real timestamp with dateOnly:true and MUST still convert, or "today" is wrong for an
  // analyst working late in the evening. A value the analyst typed as a date is rendered as
  // that date; a value that is a moment in time is converted.
  const isCalendarDate = /^\d{4}-\d{2}-\d{2}$/.test(String(iso).trim());
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...opts, timeZone: isCalendarDate ? 'UTC' : (tzIana || 'UTC'),
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

/**
 * Backend timestamps carry 6-digit microseconds and are sometimes zone-less.
 * V8 tolerates both; Firefox and Safari reject them as Invalid Date. Same fix as
 * utils/timeFormat.js normalizeIsoUtc — duplicated here rather than imported so
 * this module stays free of the wider import graph.
 */
function normalizeIso(ts) {
  if (typeof ts !== 'string') return ts;
  let s = ts.replace(/(\.\d{3})\d+/, '$1');
  if (!/[Zz]$/.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  return s;
}

/**
 * Case-entity JSON columns arrive from the API as STRINGS, not parsed values:
 * normalizeCase (client.js) passes PhishingFlags/DismissedEventIds/etc. straight
 * through and defaults them to the literal '[]' / '{}'. CaseDetail parses them into
 * React state, but this module is handed the raw `caseData`, so it must parse too.
 *
 * This is not defensive padding — `'[]'` is a truthy 2-char string, so
 * `(caseData.phishingFlags || []).map(...)` threw "map is not a function" on EVERY
 * case, breaking Generate outright and silently zeroing the modal's subject count
 * (that call site swallows the throw). Accepts an already-parsed array too, because
 * a caller that hands over CaseDetail's parsed state should also work.
 */
function asArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Every address this mailbox answers to, lowercased.
 *
 * A person is not one address. `emails_sent` used to compare SenderAddress to
 * caseData.userPrincipalName by exact string equality, and on the fixture case the
 * mailbox `bferguson@napc.md` sent 41 of its 61 messages as
 * `bferguson@southeastpcp.com` (its Entra `mail` attribute, and the address matching
 * the org name) — so a client-facing stat card reported 20 of 61, understating exposure
 * in the direction an executive reader cares about most.
 *
 * The set comes from the run blob's own `identityResolution`, which records every
 * identifier the investigation ACTUALLY searched (v1.135.0). That makes it pinned and
 * server-verifiable rather than a fresh lookup that could disagree with the data. Runs
 * predating that field, and runs where resolution failed, fall back to the case UPN —
 * the behaviour before this change, which is the right floor: never widen the match
 * beyond addresses the run itself confirmed belong to this person.
 *
 * NOTE the pipeline's SMTP proxy-address fan-out is landed but not yet consumed by the
 * runbook (memory project_multi_upn_identity_resolution), so `Identifiers` is UPN + mail
 * today. When the runbook folds aliases in, this widens with no change here.
 */
export function mailboxAddressSet(caseData, results) {
  const set = new Set();
  const add = (v) => {
    const s = String(v || '').trim().toLowerCase();
    if (s) set.add(s);
  };
  add(caseData?.userPrincipalName);
  const identity = results?.IdentityResolution;
  if (identity?.Resolved) {
    add(identity.PrimaryUpn);
    add(identity.Mail);
    (identity.Identifiers || []).forEach(add);
  }
  return set;
}

function formatDurationText(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const a = new Date(normalizeIso(startIso));
  const b = new Date(normalizeIso(endIso));
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  const mins = Math.max(0, Math.round((b - a) / 60000));
  if (mins < 1) return 'under a minute';
  if (mins < 60) return `~${mins} min`;
  const hrs = mins / 60;
  if (hrs < 24) return `~${hrs.toFixed(hrs < 10 ? 1 : 0)} hr`;
  return `~${(hrs / 24).toFixed(1)} days`;
}

/**
 * The span the page states as the attacker's period. Normally the attacker window. When no
 * token was issued it is the span of every sign-in ATTEMPT, refused ones included, widened
 * outward only (the window's own ends are kept when they are earlier/later).
 */
export function attemptWindowSpan(attackerWindow) {
  const aw = attackerWindow || {};
  let first = aw.firstAttackerActivity || '';
  let last = aw.lastAttackerActivity || '';
  if (aw.noTokenIssued) {
    const acc = aw.attackerAccess || {};
    const t = (x) => new Date(normalizeIso(x)).getTime();
    if (acc.firstAttemptAt && (!first || t(acc.firstAttemptAt) < t(first))) first = acc.firstAttemptAt;
    if (acc.lastAttemptAt && (!last || t(acc.lastAttemptAt) > t(last))) last = acc.lastAttemptAt;
  }
  return { first, last };
}

function opMatches(operation, patterns) {
  const op = String(operation || '').toLowerCase();
  return patterns.some((p) => op.includes(p));
}

/**
 * The fourteen toggle states, normalized for `filter_snapshot`.
 *
 * Read from the caller's `filterState` when supplied. The two flags the pack has always
 * received as their own parameters (`hideMsSubstrate`, `hideOutlookNoise`) are the
 * fallback for the rest, because they are applied HERE at pack-build time rather than
 * in the case's filter memo — so a caller that passes them and nothing else still
 * describes those two truthfully. Every other key is emitted as a literal `null` when
 * unknown, never as `false`: `false` would claim "the analyst had this filter off",
 * which the server would then reject as non-canonical for the wrong reason.
 */
function filterSnapshotStates(filterState, fallback) {
  const out = {};
  for (const f of CANONICAL_FILTERS) {
    if (filterState && Object.prototype.hasOwnProperty.call(filterState, f.key)) {
      out[f.key] = filterState[f.key];
    } else if (fallback && Object.prototype.hasOwnProperty.call(fallback, f.key)) {
      out[f.key] = !!fallback[f.key];
    } else {
      out[f.key] = null;
    }
  }
  return out;
}

// ── Fact extraction ─────────────────────────────────────────────────────────

/**
 * The top-level folder a mailbox path belongs to. `\Inbox\Billing\Quest` -> `\Inbox`.
 *
 * Activity is summarized by root folder only (decision 2026-08-11): thirteen paths on
 * the reference case collapse to six, and a reader gets "the Inbox and Sent Items" rather
 * than eight subfolder names. Handles a leading separator, a path with no separator, and
 * a leading/trailing space (real folders on live data include `\Alpha Training\Collections \EOD`).
 */
export function topLevelFolder(path) {
  const raw = String(path ?? '').trim();
  if (!raw) return '(unknown folder)';
  const parts = raw.split('\\').filter((p) => p.trim() !== '');
  if (parts.length === 0) return '(unknown folder)';        // the path was separators only
  return `\\${parts[0].trim()}`;
}

/**
 * The closed action vocabulary for `accessed_items`. The SERVER renders these
 * verbatim, so they are display strings, not codes — and the set is closed so a
 * reader never has to work out whether two phrasings mean the same thing.
 *
 * `Sent` is the load-bearing one. See extractAccessedItems for why it carries a
 * different evidentiary bar from every read action.
 */
export const ACCESSED_ITEM_ACTIONS = ['Read', 'Sent', 'Deleted', 'File opened', 'File downloaded'];

/**
 * Mail operations that DELETE, and the wording each one earns.
 *
 * 🔴 Every Exchange event carrying `Folders` used to be emitted as `Read`, unconditionally,
 * with the operation never inspected — so on the reference case 373 `MoveToDeletedItems`,
 * 26 `SoftDelete` and 2 `HardDelete` events rendered in a client-facing report as messages
 * the attacker READ. None of the three is in HIDDEN_EXCHANGE_OPS, so they have always
 * reached the curated view and have always been mislabelled there.
 *
 * The three flavours are kept apart because they are not the same claim: a message moved
 * to Deleted Items is recoverable by the user, a soft delete is recoverable by an admin,
 * and a hard delete is gone. "Deleted" alone would flatten a recoverable move into a purge.
 */
const MAIL_DELETE_OPS = new Map([
  ['movetodeleteditems', 'moved to Deleted Items'],
  ['softdelete', 'soft-deleted (recoverable)'],
  ['harddelete', 'hard-deleted (purged)'],
]);

/**
 * One classifier, so an Exchange event's item action is decided in exactly one place.
 * @returns {{action: string, detail: string}}
 *
 * Unknown operations keep `Read`, which is the long-standing behaviour: this change fixes
 * the deletions it can name and does not silently re-label anything else. An operation
 * that turns out to be neither is a follow-up here, not a default to guess at.
 */
export function mailItemAction(operation) {
  const op = String(operation || '').trim().toLowerCase();
  const deleteDetail = MAIL_DELETE_OPS.get(op);
  if (deleteDetail) return { action: 'Deleted', detail: deleteDetail };
  return { action: 'Read', detail: '' };
}

/** SharePoint/OneDrive operations that represent access to a specific ITEM. */
const FILE_READ_OPS = new Set(['FileAccessed', 'FileAccessedExtended', 'FilePreviewed']);
const FILE_DOWNLOAD_OPS = new Set(['FileDownloaded']);

/**
 * Placeholders synthesized here for an item the run blob left unnamed. Every one
 * MUST also appear in $script:ExecReportSyntheticSubjects (ExecutiveReportHelper.psm1)
 * or the first case containing one fails the job `redaction_failed`: the redaction
 * vocabulary is derived from the run blob, where empty values are skipped, so a
 * string synthesized here can never be in it, is never tokenized, and the residue
 * re-scan then correctly reports it as an uncovered subject. That is bug #7, and it
 * shipped because a hand-built fixture contained only real subjects.
 */
export const SYNTHETIC_ITEM_NAMES = ['(no subject)', '(unnamed file)'];

/**
 * One flat, chronological matrix of the items the attacker touched — emails read,
 * files opened, and mail sent — replacing the per-folder subject tables.
 *
 * ROW SOURCE IS THE ANALYST'S CURATED VIEW (decision 2026-08-12). `events` is the
 * filtered All Events stream, which is what the analyst approves when they generate
 * the report, so membership needs no second attribution test: the filter bar has
 * already dropped safe-IP, out-of-window, dismissed and substrate activity. That is
 * why the reference case's single file access belongs here — its IP is
 * unattributable rather than known-safe, and it lands inside the attacker window.
 *
 * SENDS ARE THE ONE EXCEPTION, and the exception is evidence-driven. On the
 * reference case four of 61 sends survive the canonical filters, and all four are
 * the real user's own routine mail relayed through Microsoft substrate IPs
 * (52.112.114.x, 2603:1036:305::) — meeting acceptances and an interview reply.
 * Rendering one of those as `Sent` in a client deliverable would assert the attacker
 * sent it, which the data does not support. So a Sent row additionally requires the
 * message's OWN recorded IP to be a case-classified malicious address:
 *   - `InferredIP` does NOT qualify. It is a temporal-proximity guess, and "the
 *     attacker sent this" is the most consequential claim in the report; a send we
 *     cannot attribute is omitted rather than asserted.
 *   - Exchange `Send` audit events are not a row source at all: all 61 on the
 *     reference case carry no Subject, no Parameters and no Folders, so they cannot
 *     populate a row. Their IP agrees with the message-trace row's.
 * The consequence is that "the attacker sent no mail" is an evidence-backed finding
 * (0 rows, stated in prose) rather than missing data.
 */
function extractAccessedItems(events, {
  maskSubjects, phishingSubjects, tzIana, maliciousSet, attackerCtx,
}) {
  // Keyed by identity so one row is one ITEM, not one access (decision 2026-08-11).
  // The reference case has 370 distinct messages across 432 item-rows; a repeat read
  // shows as a count rather than a duplicate row.
  const byKey = new Map();

  // 🔴 PER-ROW ATTRIBUTION (fact schema 1.6). The Executive REPORT renders this table as
  // the analyst's curated view -- that view is what they sign off on, and its heading sits
  // in a document whose scope notice explains it. The Executive BRIEF cannot: its page-1
  // checklist is attribution-gated line by line, and an addendum beneath a heading saying
  // "what the attacker did" that listed rows nobody can tie to the attacker would
  // contradict the page before it (plan section 9, decision 2).
  //
  // So the row carries HOW it was attributed, and the Brief's renderer filters on it. The
  // basis is `attackerBasis` -- the SAME function as the timeline badge and the checklist,
  // never a second test -- and the STRONGEST basis across the accesses that formed the row
  // wins, because a row is one item and its accesses can differ.
  //
  // ABSENT (not empty) when no access was attributable, so a reader of the pack cannot
  // mistake "we could not attribute this" for "we attributed it to nothing".
  const BASIS_RANK = { ip: 4, analyst: 3, device: 2, session: 1 };
  const strongerBasis = (a, b) => ((BASIS_RANK[b] || 0) > (BASIS_RANK[a] || 0) ? b : a);

  const upsert = (key, row, iso, basis) => {
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...row, _firstIso: iso, accessCount: 1, _basis: basis || '',
        _folders: new Set(row.folder ? [row.folder] : []),
      });
      return;
    }
    existing.accessCount += 1;
    if (row.folder) existing._folders.add(row.folder);
    if (basis) existing._basis = strongerBasis(existing._basis, basis);
    // First access wins the timestamp; a later access only raises the count.
    if (iso && (!existing._firstIso || String(iso) < String(existing._firstIso))) {
      existing._firstIso = iso;
      existing.timeDisplay = row.timeDisplay;
    }
    // A note found on any access applies to the item.
    if (row.note && !existing.note) existing.note = row.note;
  };

  for (const evt of events) {
    const iso = evt.EventTimeUTC || '';
    const timeDisplay = formatFactTime(iso, tzIana);
    // Computed ONCE per event, before the per-type branches: every row this event
    // produces shares the event's basis.
    const evtBasis = attackerCtx ? (attackerBasis(evt, attackerCtx)?.basis || '') : '';

    // ── Mail reads and deletions ────────────────────────────────────────
    if (evt._eventType === 'Exchange') {
      const parsed = parseFolders(evt.Folders);
      if (!parsed) continue;
      // ONE classifier decides the action (see mailItemAction) — the branch used to
      // hardcode 'Read' here and then `continue`, which is how deletions came to be
      // reported as reads.
      const { action, detail } = mailItemAction(evt.Operation);
      for (const folder of (Array.isArray(parsed) ? parsed : [parsed])) {
        const path = String(folder?.Path || '').trim();
        for (const item of folder?.FolderItems || []) {
          const key = item.InternetMessageId || item.Id || item.Subject || '';
          if (!key) continue;
          const notes = [];
          if (item.SizeInBytes >= 5 * 1024 * 1024) notes.push(`large attachment (~${(item.SizeInBytes / 1048576).toFixed(1)} MB)`);
          if (phishingSubjects.has(String(item.Subject || '').toLowerCase())) notes.push('marked by the analyst as the phishing entry point');
          // 🔴 The ACTION and its DETAIL are part of the identity. The key was `mail::${key}`
          // alone, so a message that was read and later deleted merged into ONE row that
          // kept whichever action was inserted first — the addendum's whole purpose is to
          // say what was done to each item, and it silently dropped one of the two.
          upsert(`mail::${action}::${detail}::${key}`, {
            timeDisplay,
            item: maskSubjects ? '' : (item.Subject || '(no subject)'),
            folder: path,
            action,
            detail,
            note: notes.join('; '),
          }, iso, evtBasis);
        }
      }
      continue;
    }

    // ── File reads ──────────────────────────────────────────────────────
    // SharePoint events carry the SAME `Folders` field, with FileName/Path in place
    // of FolderItems — which is also why the redaction vocabulary already walks it.
    if (evt._eventType === 'SharePoint' || evt._eventType === 'SharePointList') {
      const isRead = FILE_READ_OPS.has(evt.Operation);
      const isDownload = FILE_DOWNLOAD_OPS.has(evt.Operation);
      if (!isRead && !isDownload) continue;              // PageViewed / SharingSet are not item access
      const parsed = parseFolders(evt.Folders);
      if (!parsed) continue;
      for (const node of (Array.isArray(parsed) ? parsed : [parsed])) {
        const name = String(node?.FileName || node?.SourceFileName || '').trim();
        const path = String(node?.Path || '').trim();
        if (!name && !path) continue;
        // Identity is (name, PATH, action). Keying on the name alone merged two
        // different documents that happen to share a file name across sites into one
        // row listing both locations — caught against the real run blob, where it
        // inflated the multi-location count from the true 224 to 229.
        upsert(`file::${name.toLowerCase()}::${path.toLowerCase()}::${isDownload ? 'dl' : 'rd'}`, {
          timeDisplay,
          item: maskSubjects ? '' : (name || '(unnamed file)'),
          folder: path,
          action: isDownload ? 'File downloaded' : 'File opened',
          note: '',
        }, iso, evtBasis);
      }
      continue;
    }

    // ── Sends (attacker-attributed only — see the header) ───────────────
    // `_eventType === 'MessageTrace'` is already Direction='Sent' only
    // (combineAndTagEvents). Trace rows are PER-RECIPIENT grain, so identity is the
    // MessageId and recipients collapse into a count.
    if (evt._eventType === 'MessageTrace') {
      const ip = String(evt.IPAddress || '').trim();
      if (!ip || !maliciousSet.has(ip)) continue;
      const key = `sent::${evt.MessageId || evt.EventId || evt.Subject || ''}`;
      if (key === 'sent::') continue;
      const row = byKey.get(key);
      const recipient = String(evt.RecipientAddress || '').trim();
      if (row) {
        if (recipient) row._recipients.add(recipient);
        continue;                                        // a second recipient is not a second access
      }
      byKey.set(key, {
        timeDisplay,
        item: maskSubjects ? '' : (evt.Subject || '(no subject)'),
        folder: '',
        action: 'Sent',
        // The join key for the send->delete chain the SERVER derives from the pinned blob.
        // An opaque RFC message id: not content, not a subject, and the same identifier
        // LogSubjectView already records as metadata. Carried only on Sent rows -- it exists
        // so the server can attach its chain to THIS row rather than emit a parallel table
        // the reader has to correlate by eye.
        messageId: String(evt.MessageId || ''),
        note: '',
        ip,
        _firstIso: iso,
        accessCount: 1,
        // A Sent row exists ONLY when the message's own recorded address is a
        // case-classified malicious one (see the header), so its basis is 'ip' by
        // construction -- it is not read from evtBasis, which could name a weaker basis
        // for the same event and would understate the strongest claim in the document.
        _basis: 'ip',
        _folders: new Set(),
        _recipients: new Set(recipient ? [recipient] : []),
      });
    }
  }

  // Chronological — the order an incident is read in, and the order that answers
  // "what did they touch, in what sequence".
  const items = [...byKey.values()]
    .sort((a, b) => String(a._firstIso || '').localeCompare(String(b._firstIso || '')))
    .map((r) => {
      const folders = [...(r._folders || [])].filter(Boolean);
      const recipients = [...(r._recipients || [])].filter(Boolean);
      const out = {
        timeDisplay: r.timeDisplay || '',
        folder: folders.length > 1 ? folders.join('; ') : (folders[0] || r.folder || ''),
        action: r.action,
        accessCount: String(r.accessCount || 1),
        note: r.note || '',
      };
      // The key is ABSENT on a masked run rather than empty: "" would be
      // indistinguishable from an item whose name the run blob left blank, and it
      // would ship 370 useless leaves to the provider. The row itself still goes —
      // time, place, action and count are not sensitive.
      if (r.item) out.item = r.item;
      // Absent rather than empty, for the same reason as `item`: a blank leaf on 1,387
      // rows is pure payload. Only a deletion carries one today (which flavour of delete).
      if (r.detail) out.detail = r.detail;
      // Absent when the row is not attributable -- which is what the Brief's addenda
      // filter on, and the safe direction: an absent basis omits the row rather than
      // asserting it under a heading that says the attacker did it.
      if (r._basis) out.basis = r._basis;
      if (recipients.length) {
        out.recipients = recipients.length > 2
          ? `${recipients.slice(0, 2).join(', ')} +${recipients.length - 2} more`
          : recipients.join(', ');
      }
      return out;
    });

  return { items, first: items[0]?.timeDisplay || '', last: items[items.length - 1]?.timeDisplay || '' };
}

/**
 * The analyst's PHISHING ENTRY POINT — the "how did this start" fact.
 *
 * Two independent markers, and **either one alone must produce the section** (decision
 * 2026-08-11: "sometimes we have Message Trace but not URL clicks"). They are not gated
 * on each other and no causal link between them is asserted — a click ~11 hours after a
 * delivered lure may or may not be that lure, and the analyst marked two things, not a
 * chain. Correlating them would be the report inferring what a human did not state.
 *
 *   phishingFlags       -> the entry-point EMAIL   (Message Trace "Phish" badge)
 *   urlClassifications  -> the confirmed CLICK     (URL Clicks tab "Class" badge)
 *
 * ── Why this reads the RAW arrays, not the tagged `events` stream ────────────────
 * `combineAndTagEvents` keeps only `Direction === 'Sent'` message-trace rows, so the
 * tagged stream contains no inbound mail at all — the flagged lure is inbound, and
 * looking for it there finds nothing. (The case UI works because it re-injects
 * phishing-flagged received rows separately; the pack builder never did.) URL clicks
 * come from the raw array for a different reason: the URL Clicks tab is deliberately
 * exempt from the global filter toggles, so the classification the analyst made was
 * made against the unfiltered set.
 *
 * ── Identity ────────────────────────────────────────────────────────────────────
 * A flag's `eventId` is the message-trace COMPOSITE `Id|RecipientAddress` (that feed is
 * per-recipient grain), so it is matched whole and never split on `|`. The previous
 * behaviour re-matched by lowercased SUBJECT text, which tagged every message sharing a
 * subject and tagged nothing at all when the subject was empty.
 *
 * Clicks are de-duplicated by URL: two confirmed click events on one link are one
 * finding, and the reference case has exactly that (2 eventIds -> 1 URL). The earliest
 * click wins the timestamp and `clickCount` records the rest.
 *
 * An empty subject is OMITTED rather than synthesized into a placeholder — a
 * synthesized literal cannot be in a redaction vocabulary derived from the run blob, so
 * it would fail the residue scan as an uncovered subject (that is bug #7's exact shape).
 * The renderer supplies "(no subject)" server-side, where nothing redacts it.
 */
function extractPhishingEntryPoint({
  caseData, results, filteredResults, urlClassifications, maskSubjects, tzIana,
  attackerWindow,
}) {
  const trace = filteredResults?.MessageTraceEvents || results?.MessageTraceEvents || [];
  const clickEvents = filteredResults?.UrlClickEvents || results?.UrlClickEvents || [];
  const liveFlags = asArray(caseData?.phishingFlags);

  // ── Quarantine release + wider delivery ─────────────────────────────────
  // 🔴 Both are derived from the RAW arrays through the shared util, never from the
  // curated view: the release row's only address is an inferred Microsoft IP classified
  // safe, so under the report's own canonical filter state it is not in `filteredResults`
  // unless CaseDetail's re-injection put it back — and this derivation must not depend on
  // that. One derivation (utils/quarantineRelease.js), three consumers: the timeline, this
  // pack, and the Client Report.
  const rawGeneral = results?.GeneralEvents || [];
  const rawTrace = results?.MessageTraceEvents || [];
  const flaggedIndex = flaggedPhishMessageIndex(liveFlags, rawTrace);
  const releaseEvidence = collectReleaseEvidence(rawGeneral);
  // silver id -> the flag's eventId, so a release can be attached to the EMAIL it
  // describes rather than to a single top-level object. With two flagged emails and one
  // released, a top-level object cannot say which.
  const releaseByFlagEventId = new Map();
  for (const [silverId, info] of flaggedIndex.entries()) {
    const ev = releaseEvidence.get(silverId);
    if (!ev) continue;
    releaseByFlagEventId.set(info.eventId, {
      timeDisplay: ev.timeUtc ? formatFactTime(ev.timeUtc, tzIana) : '',
      reportedFalsePositive: !!ev.reportedFalsePositive,
    });
  }

  // ── The lure ────────────────────────────────────────────────────────────
  const traceById = new Map();
  for (const e of trace) {
    const id = String(e?.EventId || '').trim();
    if (id) traceById.set(id, e);
  }
  const emails = [];
  for (const flag of asArray(caseData?.phishingFlags)) {
    const eventId = String(flag?.eventId || '').trim();
    if (!eventId) continue;
    const evt = traceById.get(eventId);
    // The flag itself carries sender + subject, so the finding survives even when the
    // run blob no longer holds the row (a re-run with a narrower date range). What only
    // the event can give — recipient, delivery status, sending IP, time — is then blank,
    // and the renderer states the finding without them rather than dropping it.
    const subject = String(flag?.subject || evt?.Subject || '').trim();
    const row = {
      eventId,
      sender: String(flag?.senderAddress || evt?.SenderAddress || '').trim(),
      recipient: String(evt?.RecipientAddress || '').trim(),
      status: String(evt?.Status || '').trim(),
      ip: String(evt?.IPAddress || '').trim(),
      timeDisplay: evt?.EventTimeUTC ? formatFactTime(evt.EventTimeUTC, tzIana) : '',
    };
    if (!maskSubjects && subject) row.subject = subject;
    // 🔴 ATTACHED TO THE EMAIL IT DESCRIBES. Carries no sender or subject of its own —
    // the row it hangs off already names the lure — so a masked run is safe by
    // construction and nothing new leaves the browser.
    const rel = releaseByFlagEventId.get(eventId);
    if (rel) row.quarantineRelease = rel;
    emails.push(row);
    if (emails.length >= CAPS.phishEmails) break;
  }

  // ── Which flagged email a click came from (schema 1.23) ─────────────────
  // 🔴 AN EXACT JOIN, NOT A JUDGMENT. A URL-click record carries the Exchange
  // NetworkMessageId of the message the link was in, and a message-trace row's id IS that
  // NetworkMessageId (SilverRecordId, or the EventId prefix before `|recipient`). Before this
  // the Brief said "the records do not establish that the click came from that message" on a
  // case where both clicks named the flagged email's id (IR-20261013-levig-001, pipeline
  // review A3). No time, sender or subject proximity is ever used: no id match, no link.
  const emailBySilverId = new Map();
  for (const em of emails) {
    const evt = traceById.get(em.eventId);
    const sid = String(evt?.SilverRecordId || evt?.RawLog?.id || em.eventId.split('|')[0] || '').trim().toLowerCase();
    if (sid) emailBySilverId.set(sid, em.eventId);
  }
  const clickNetworkMessageId = (evt) => String(evt?.NetworkMessageId || evt?.RawLog?.NetworkMessageId || '').trim().toLowerCase();

  // ── The click ───────────────────────────────────────────────────────────
  const clickById = new Map();
  for (const e of clickEvents) {
    const id = String(e?.EventId || '').trim();
    if (id) clickById.set(id, e);
  }
  const byUrl = new Map();
  for (const [eventId, verdict] of Object.entries(urlClassifications || {})) {
    if (verdict !== 'confirmed') continue;
    const evt = clickById.get(String(eventId).trim());
    if (!evt) continue;                       // unresolvable; the server verifies too
    const url = String(evt.Url || '').trim();
    if (!url) continue;
    const key = url.toLowerCase();
    const iso = String(evt.EventTimeUTC || '');
    // Field names verified against the real run blobs, not assumed: the clicking user is
    // `AccountUpn` (there is no `UserId` on this workload — reading one gave an empty
    // column on all three fixture cases), and `ActionType` is load-bearing rather than
    // decorative: it records whether Safe Links ALLOWED or BLOCKED the click, which is
    // the difference between "the user reached the attacker's page" and "they were
    // stopped". All three fixtures read `ClickAllowed`, so a blocked click is untested —
    // the renderer therefore states the raw verdict rather than interpreting it.
    const row = {
      eventId: String(eventId).trim(),
      url,
      timeDisplay: iso ? formatFactTime(iso, tzIana) : '',
      accountUpn: String(evt.AccountUpn || evt.UserId || '').trim(),
      ip: String(evt.IPAddress || evt.InferredIP || '').trim(),
      actionType: String(evt.ActionType || '').trim(),
      threatType: String(evt.ThreatType || '').trim(),
    };
    const fromEmail = emailBySilverId.get(clickNetworkMessageId(evt));
    if (fromEmail) row.fromEmailEventId = fromEmail;
    const existing = byUrl.get(key);
    if (!existing) {
      byUrl.set(key, { ...row, clickCount: 1, _iso: iso });
      continue;
    }
    existing.clickCount += 1;
    // Any click on this URL tying back to a flagged email links the row; one without the id
    // never un-links it.
    if (fromEmail && !existing.fromEmailEventId) existing.fromEmailEventId = fromEmail;
    if (iso && (!existing._iso || iso < existing._iso)) {
      Object.assign(existing, row, { clickCount: existing.clickCount, _iso: iso });
    }
  }
  const clicks = [...byUrl.values()]
    .sort((a, b) => String(a._iso || '').localeCompare(String(b._iso || '')))
    .slice(0, CAPS.phishClicks)
    .map(({ _iso, ...rest }) => ({ ...rest, clickCount: String(rest.clickCount) }));

  // ── Other accounts to review ────────────────────────────────────────────
  // 🔴 A LEAD, NOT A COMPROMISE CLAIM (operator, 2026-09-02). The runbook's Related
  // Recipients query is SENDER-WIDE over the case date range — it has no subject or
  // campaign constraint — so these rows are "mail from the flagged sender to other
  // accounts", never "the same email" and never "these accounts were phished". Every
  // rendering of it says "accounts to review". The GOAL is to tell the investigator where
  // to look next.
  //
  // Scope is the operator's: earliest→latest SUCCESSFUL activity from analyst-flagged
  // malicious IPs across BOTH sign-in feeds, ±5 days. With no malicious IP classified the
  // basis is undefined, and then `related_scope` says `case_range` so the caption states
  // the reviewed period instead of making a false ±5-day claim.
  const bounds = maliciousActivityBounds(attackerWindow);
  const selection = selectRelatedMessages(relatedPhishingRows(results), liveFlags, bounds);
  const relatedRows = [];
  // The analyst-confirmed malicious links, so another recipient's click on the SAME link can be
  // called that ("clicked the malicious link") rather than only "a link in it".
  const confirmedClickUrls = new Set(clicks.map((c) => String(c?.url || '').trim().toLowerCase()).filter(Boolean));
  for (const r of selection.rows) {
    const silverId = relatedMessageSilverId(r);
    const row = {
      // 🔴 STRUCTURAL, never rendered, stripped from the model projection. The final
      // runbook projection carries NO top-level SilverRecordId on these rows, so without
      // the verbatim composite EventId the server cannot match a row at all — and two
      // same-recipient/same-status rows are valid and ambiguous.
      sourceEventId: String(r?.EventId || '').trim(),
      recipient: String(r?.RecipientAddress || '').trim(),
      status: String(r?.Status || '').trim(),
      timeDisplay: r?.EventTimeUTC ? formatFactTime(r.EventTimeUTC, tzIana) : '',
    };
    // 🔴 ABSENT, never `false`. Other users' actions are outside this case's collection
    // scope, so absence of a release event is not evidence there was no release — and
    // "not released" is a claim we cannot make. Present only when a release event in THIS
    // case's data names that message.
    if (silverId && releaseEvidence.has(silverId)) row.released = true;
    // 1.24 -- ABSENT unless true, like `released`. The server re-derives all three from the
    // pinned blob and overwrites them; only the click's display TIME is the client's, because
    // the server cannot localize a timestamp.
    if (relatedIsSameMessage(r, flaggedIndex)) row.sameMessage = true;
    const rc = relatedRecipientClicks(r);
    if (rc.length > 0) {
      row.clickCount = rc.length;
      if (rc[0]?.EventTimeUTC) row.firstClickDisplay = formatFactTime(rc[0].EventTimeUTC, tzIana);
      if (rc.some((c) => confirmedClickUrls.has(String(c?.Url || '').trim().toLowerCase()))) {
        row.clickedConfirmedLink = true;
      }
    }
    relatedRows.push(row);
  }

  // The FULL in-scope population's ids, PRE-cap. The server recomputes the printed totals
  // from the ids it can verify — a client-supplied count under loose whole-blob bounds is
  // not a provable number, and the page states it as exact.
  const relatedIds = selection.rows
    .map((r) => String(r?.EventId || '').trim())
    .filter(Boolean)
    .slice(0, CAPS.relatedMessageIds);

  const out = { emails, clicks };
  if (relatedRows.length > 0) {
    out.related_messages = relatedRows.slice(0, CAPS.relatedMessages);
    out.related_message_ids = relatedIds;
    out.related_scope = selection.scope;
    // The bounds the caption prints, pre-formatted like every other time in the pack.
    if (selection.scope === RELATED_SCOPE_PAD && bounds) {
      out.related_scope_from = formatFactTime(bounds.startUtc, tzIana);
      out.related_scope_to = formatFactTime(bounds.endUtc, tzIana);
    } else {
      const from = caseData?.startDate || '';
      const to = caseData?.endDate || '';
      if (from) out.related_scope_from = String(from).substring(0, 10);
      if (to) out.related_scope_to = String(to).substring(0, 10);
    }
  }
  return out;
}

function extractMailboxDetail(events) {
  // Group mailbox access by TOP-LEVEL folder. COUNTS ONLY — the per-folder subject
  // lists this used to carry now live in the flat `accessed_items` matrix, so item
  // text has exactly one home in the pack and the report cannot render it twice with
  // two different de-duplications.
  //
  // The roll-up re-deduplicates AT THE ROOT rather than summing subfolder counts,
  // because messages genuinely recur across folders: the reference case has 370 subject
  // rows for 276 distinct messages, and one message appeared in both `\Inbox` and
  // `\Sent Items`. Summing children would have claimed 221 messages for an `\Inbox`
  // subtree that holds fewer. Identity is the MESSAGE (decision 2026-08-11), so a
  // message read from two subfolders of one root counts once in that root.
  const folders = new Map();
  let accessEvents = 0;
  const messageKeys = new Set();

  for (const evt of events) {
    if (evt._eventType !== 'Exchange') continue;
    const parsed = parseFolders(evt.Folders);
    if (!parsed) continue;
    accessEvents += 1;
    const accessType = evt.Operation === 'MailItemsAccessed' ? getMailAccessType(evt) : '';
    const list = Array.isArray(parsed) ? parsed : [parsed];

    for (const folder of list) {
      const root = topLevelFolder(folder?.Path);
      if (!folders.has(root)) {
        folders.set(root, { folder: root, distinctMessages: 0, accessEvents: 0, itemTypes: new Set(), _seen: new Set() });
      }
      const bucket = folders.get(root);
      bucket.accessEvents += 1;
      if (accessType) bucket.itemTypes.add(accessType);
      else if (evt.Operation) bucket.itemTypes.add(evt.Operation);

      for (const item of folder?.FolderItems || []) {
        const key = item.InternetMessageId || item.Id || item.Subject || '';
        if (!key) continue;
        // Keyed by ROOT, so the case-wide distinct count and the per-folder counts agree
        // on what "one message" is.
        messageKeys.add(`${root}::${key}`);
        if (bucket._seen.has(key)) continue;
        bucket._seen.add(key);
        bucket.distinctMessages += 1;
      }
    }
  }

  const out = [...folders.values()]
    .sort((a, b) => b.distinctMessages - a.distinctMessages)
    .map((f) => ({
      folder: f.folder,
      distinctMessages: String(f.distinctMessages),
      accessEvents: String(f.accessEvents),
      itemTypes: [...f.itemTypes].join(', '),
    }));

  return { folders: out, accessEvents, distinctMessages: messageKeys.size };
}

function extractInboxRules(events, tzIana) {
  return events
    .filter((e) => INBOX_RULE_OPS.has(e.Operation))
    .map((e) => ({
      name: extractRuleName(e) || '(unnamed rule)',
      createdDisplay: formatFactTime(e.EventTimeUTC, tzIana),
      ip: e.IPAddress || e.InferredIP || '',
      actions: summarizeRuleActions(e),
      operation: e.Operation,
    }));
}

function extractRuleName(evt) {
  try {
    const params = typeof evt.Parameters === 'string' ? JSON.parse(evt.Parameters) : evt.Parameters;
    const named = (params || []).find((p) => /^name$/i.test(p.Name));
    if (named?.Value) return String(named.Value);
  } catch { /* a malformed parameter blob must not break the pack */ }
  return '';
}

function summarizeRuleActions(evt) {
  const actions = [];
  try {
    const params = typeof evt.Parameters === 'string' ? JSON.parse(evt.Parameters) : evt.Parameters;
    for (const p of params || []) {
      const n = String(p.Name || '').toLowerCase();
      if (n.includes('deletemessage')) actions.push('deletes messages');
      else if (n.includes('markasread')) actions.push('marks messages as read');
      else if (n.includes('movetofolder')) actions.push(`moves messages to ${p.Value}`);
      else if (n.includes('forwardto') || n.includes('redirectto')) actions.push(`forwards to ${p.Value}`);
      else if (n.includes('stopprocessingrules')) actions.push('stops further rule processing');
    }
  } catch { /* ignore */ }
  return actions.join(', ');
}

// ═══════════════════════════════════════════════════════════════════════════
// The Executive BRIEF's page-1 checklist
// ═══════════════════════════════════════════════════════════════════════════
//
// One line per category of thing an attacker can do, each resolving to one of THREE
// states. The third is not optional and is the whole reason this is not a set of counts:
//
//   yes             >= 1 attacker-attributed event of that kind
//   no              the source was READ and nothing was attributed -- whether it held rows
//                   that could discriminate, or held nothing at all
//   not_determined  we could not look: the query FAILED, or the rows carry nothing that could
//                   have matched, so the data supports the claim in NEITHER direction
//
// 🔴 THE DIVIDING LINE IS WHETHER WE COULD LOOK, never what we found. Two operator findings
// pushed it here (2026-08-24): rows existing with none attributed was returning
// `not_determined` when that is the definition of `no`, and a workload with ZERO rows from a
// query that SUCCEEDED was doing the same -- printing "Not determined" beside a note reading
// "no SharePoint and OneDrive activity was recorded", a row that states a definite observation
// and then declines to answer it. `not_determined` must never mean "we looked and found
// nothing".
//
// 🔴 "No" and "not determined" are different claims and must never collapse. On the
// reference case, deleted mail is the first (401 delete events exist, none attributed --
// and Exchange DOES carry attributed rows, so "no" is a real finding) and files are the
// second (1,261 SharePoint rows, and not ONE row in the whole workload can be tied to or
// excluded from the attacker). Printing "No" for files would assert the attacker read no
// files, which the data cannot support.
//
// That is why the discriminating test is at the WORKLOAD level, not the category level:
// "can this source distinguish the attacker at all?" A category with zero attributed
// events inside a workload that has attributed events elsewhere is a genuine No.
//
// 🔴 EVERY LINE IS ATTRIBUTION-GATED. No line may be derived from a presence count. This
// is the fourth guard of one defect shape -- v1.156.4 (`apps[]` carried the whole
// account's applications), v1.157.0 (`mfa_changes` filed the incident RESPONSE under
// "what the attacker changed"), v1.159.0 (`files_accessed` counted the real user's own
// SharePoint activity) -- and on a one-page checklist it degrades from a misleading number
// in a table to a bare "read files: yes", which is worse.
//
// Attribution is `attackerBasis` from utils/attackerAttribution.js -- ONE implementation
// shared with the timeline badge, so the Brief and the screen cannot disagree about what
// is the attacker's. The BASIS MIX travels with every line because a session is
// corroboration and not proof, and that module says in red that basis must never be
// flattened to a boolean: a session-only line is reported as such, never as an
// unqualified Yes.
export const ATTACKER_ACTION_IDS = [
  'read_mail', 'sent_mail', 'deleted_mail', 'inbox_rules', 'files',
  'signin_methods', 'devices_joined', 'other_identities', 'enterprise_apps',
];

// Actions this account took against OTHER identities. Deliberately NOT "compromised other
// users": a case is scoped to one mailbox, so asserting another account was compromised
// would be unsupported by construction (plan section 3). What IS derivable is the grants
// and invitations made from this account.
const IDENTITY_ACTION_OPS = [
  'add-mailboxpermission', 'add-recipientpermission', 'add-mailboxfolderpermission',
  'set-mailboxfolderpermission', 'modifyfolderpermissions',
  'invite external user', 'add member to role', 'add member to group',
  'add owner to group', 'add app role assignment grant to user',
];

// Which source each category is read from. `workload` groups categories that share a
// source, because the not_determined test is asked of THE SOURCE -- could it be read, and
// could its rows have matched -- never of the outcome.
const ACTION_SPECS = [
  {
    id: 'read_mail', label: 'Read mail in the mailbox', workload: 'exchange',
    isCategory: (e) => e._eventType === 'Exchange' && mailItemAction(e.Operation).action === 'Read',
  },
  {
    id: 'deleted_mail', label: 'Deleted mail', workload: 'exchange',
    isCategory: (e) => e._eventType === 'Exchange' && mailItemAction(e.Operation).action === 'Deleted',
  },
  {
    // Outbound mail reaches the curated stream as message-trace rows (Exchange's own
    // Send is in HIDDEN_EXCHANGE_OPS -- feed selection, not noise suppression).
    id: 'sent_mail', label: 'Sent mail from the mailbox', workload: 'message_trace',
    isCategory: (e) => e._eventType === 'MessageTrace',
  },
  {
    id: 'inbox_rules', label: 'Created or changed inbox rules', workload: 'exchange_admin',
    isCategory: (e) => INBOX_RULE_OPS.has(e.Operation),
  },
  {
    id: 'files', label: 'Opened or downloaded files', workload: 'sharepoint',
    isCategory: (e) => (e._eventType === 'SharePoint' || e._eventType === 'SharePointList')
      && (FILE_READ_OPS.has(e.Operation) || FILE_DOWNLOAD_OPS.has(e.Operation)),
  },
  {
    id: 'signin_methods', label: 'Added or changed sign-in methods', workload: 'directory_audit',
    isCategory: (e) => e._eventType === 'DirectoryAudit' && opMatches(e.Operation, MFA_OPS),
  },
  {
    id: 'other_identities', label: 'Acted against other identities', workload: 'directory_audit',
    isCategory: (e) => opMatches(e.Operation, IDENTITY_ACTION_OPS),
  },
];

const WORKLOAD_LABELS = {
  exchange: 'mailbox',
  exchange_admin: 'mailbox administration',
  message_trace: 'message trace',
  sharepoint: 'SharePoint and OneDrive',
  directory_audit: 'directory audit',
  signin: 'sign-in',
};

// 🔴 The checklist's workload names map to the RUN's canonical workload keys — the same
// keys `Register-DataSourceFailure` writes into `dataSourceErrors` and the same ones
// `Get-ExecReportRunWorkloadTotals` bounds counts against. Both sides must agree or a
// failed source silently reads as "nothing happened", which is the exact false negative
// `dataSourceErrors` exists to prevent (see the DataSourceErrors contract in CLAUDE.md).
//
// `exchange_admin` is this module's own sub-division of `exchange` — inbox-rule cmdlets
// are Exchange events, but "no rule was created" is a finding that does not depend on
// mail reads being attributable — so it maps back to the same source key.
export const CHECKLIST_WORKLOAD_SOURCES = {
  exchange: ['exchange'],
  exchange_admin: ['exchange'],
  message_trace: ['message_trace'],
  sharepoint: ['sharepoint'],
  directory_audit: ['directory_audit'],
  signin: ['signin', 'graph_signin'],
};

/**
 * Build the checklist.
 *
 * @param events        the analyst's curated stream (already filtered + tagged)
 * @param attackerCtx   the ONE shared attribution context
 * @param attackerDeviceRows  the pack's own attacker_devices array (one row per machine)
 * @param attackerApps  the attacker-scoped application set
 * @param failedWorkloads  canonical log_type names whose query FAILED in the run
 */
export function buildAttackerActions({
  events, attackerCtx, attackerDeviceRows, attackerApps, failedWorkloads,
  phishingPages = [], noTokenIssued = false, requestedApps = [],
}) {
  const rows = [];
  // Canonical run workload keys whose query FAILED. Resolved through
  // CHECKLIST_WORKLOAD_SOURCES so a checklist workload is "failed" when ANY of its source
  // keys failed.
  const failedKeys = failedWorkloads instanceof Set ? failedWorkloads : new Set(failedWorkloads || []);
  const failed = new Set();
  for (const [w, keys] of Object.entries(CHECKLIST_WORKLOAD_SOURCES)) {
    if (keys.some((k) => failedKeys.has(k))) failed.add(w);
  }

  // Does each workload contain ANY row the attacker can be tied to? Computed ONCE per
  // workload over the curated stream, because two categories reading the same source must
  // reach the same verdict about whether that source can discriminate.
  const workloadRows = new Map();
  const workloadAttributed = new Map();
  // How many rows COULD have been attributed -- the measurement that separates "No" from
  // "Not determined". See attackerBasisCapable.
  const workloadCapable = new Map();
  for (const spec of ACTION_SPECS) {
    if (!workloadRows.has(spec.workload)) {
      workloadRows.set(spec.workload, 0);
      workloadAttributed.set(spec.workload, 0);
    }
  }
  const workloadOf = (e) => {
    if (e._eventType === 'Exchange') return 'exchange';
    if (e._eventType === 'MessageTrace') return 'message_trace';
    if (e._eventType === 'SharePoint' || e._eventType === 'SharePointList') return 'sharepoint';
    if (e._eventType === 'DirectoryAudit') return 'directory_audit';
    if (e._eventType === 'SignIn' || e._eventType === 'NonInteractiveSignIn') return 'signin';
    return '';
  };
  for (const e of events) {
    // Inbox-rule cmdlets are Exchange-admin events that arrive tagged as Exchange; they
    // are their own workload for this purpose because "no rule was created" is a finding
    // that does not depend on mail reads being attributable.
    const w = INBOX_RULE_OPS.has(e.Operation) ? 'exchange_admin' : workloadOf(e);
    if (!w || !workloadRows.has(w)) continue;
    workloadRows.set(w, workloadRows.get(w) + 1);
    if (attackerBasis(e, attackerCtx)) {
      workloadAttributed.set(w, workloadAttributed.get(w) + 1);
    }
    // Whether a match was POSSIBLE -- see attackerBasisCapable. This is the measurement that
    // separates "No" from "Not determined"; the attributed count above cannot.
    if (attackerBasisCapable(e)) {
      workloadCapable.set(w, (workloadCapable.get(w) || 0) + 1);
    }
  }

  const resolve = ({ id, label, workload, count, basisMix, note, unattributed = 0 }) => {
    const total = (basisMix.ip || 0) + (basisMix.analyst || 0) + (basisMix.device || 0) + (basisMix.session || 0);
    // The invariant the server re-checks: the mix accounts for every counted event.
    const mix = total === count ? basisMix : { ip: 0, analyst: 0, device: 0, session: 0 };
    let state = 'no';
    let why = note || '';
    if (count > 0) {
      state = 'yes';
    } else if (failed.has(workload)) {
      state = 'not_determined';
      why = `the ${WORKLOAD_LABELS[workload] || workload} log could not be read for this investigation`;
    } else if (workloadRows.get(workload) > 0 && workloadAttributed.get(workload) === 0) {
      // 🔴 ROWS EXIST AND NONE WAS ATTRIBUTED. That is the same observation for BOTH remaining
      // states, so it cannot choose between them -- which is the bug this branch used to have:
      // it returned `not_determined` on the strength of it, and on the reference case printed
      // "Not determined" over 1,261 SharePoint and OneDrive rows that every one carry a real
      // address (1,175 of them the user's own residential IP) and 1,219 of which carry a
      // session. The source discriminates fine; the attacker is not in it. That is "No".
      //
      // The deciding question is whether the source could have matched AT ALL, asked with the
      // same accessors attackerBasis uses (attackerBasisCapable), so the capability test and
      // the attribution test cannot drift apart.
      const n = workloadRows.get(workload);
      const capable = workloadCapable.get(workload) || 0;
      const label = WORKLOAD_LABELS[workload] || workload;
      const plural = n === 1 ? '' : 's';
      if (capable === 0) {
        // Nothing to match on anywhere in the workload: the data supports the claim in
        // neither direction. Deliberately terse -- this renders in a ~272px column on the
        // Brief's fixed-length page 1, where a wrapped line costs 18px.
        // 🔴 "either way" stays. Trimmed to "none attributable" this reads as "No", which is
        // the collapse the third state exists to prevent.
        state = 'not_determined';
        why = `${n} ${label} event${plural}; none attributable either way`;
      } else {
        // A real "No": the source can distinguish the attacker and did not place them here.
        state = 'no';
        // 🔴 `n` COUNTS THE ANALYST'S CURATED VIEW, NOT THE RUN (pipeline review B4): "1 message
        // trace event reviewed" sat over a run holding 121 and read as "only one message was
        // looked at". The Brief's checklist column header says so -- "Based on the
        // incident-related records" (Format-ExecBriefChecklist) -- once, rather than in every
        // note, because every note gaining two words wrapped every row and overflowed page 1.
        why = `${n} ${label} event${plural} reviewed; none was the attacker's`;
        if (capable < n) {
          // Partial capability must not vanish into a clean "No" -- the same reasoning as the
          // `unattributed` branch below. Say how much of the workload could not be judged.
          why += `; ${n - capable} not attributable either way`;
        }
      }
    } else if (workloadRows.get(workload) === 0) {
      // 🔴 NO ROWS AT ALL, FROM A QUERY THAT SUCCEEDED -- that is "No", not "not determined"
      // (operator, 2026-08-24; second time on this state). The `failed` branch above already
      // owns every workload we could NOT read, which is what `not_determined` is for. Reaching
      // here means the source was read and had nothing in it, and printing "Not determined"
      // beside a note reading "no SharePoint and OneDrive activity was recorded" is a row that
      // contradicts itself: it states a definite observation and then declines to answer it.
      //
      // THE RULE: `not_determined` means WE COULD NOT LOOK. It must never mean WE LOOKED AND
      // FOUND NOTHING. The two remaining ways to earn it are a failed query (above) and rows
      // that carry nothing matchable (the capable === 0 branch above).
      state = 'no';
      // 🔴 "in the reviewed period" WAS A CLAIM THIS NOTE CANNOT MAKE (operator, 2026-08-25).
      // It rendered as "no SharePoint and OneDrive activity was recorded in the reviewed
      // period" on a case where that feed's warehouse records begin two days AFTER
      // containment and 5,438 rows were retrieved out of band and swept clean. So the note
      // asserted a period-wide absence on the strength of records that do not span the
      // period -- the same false closure the coverage correction exists to prevent, written
      // by the OTHER author. It now says what the count is actually over: the records
      // reviewed. Whether those span the period is the coverage correction's business, and
      // it is stated on this same row.
      // 🔴 "TIED TO THE ATTACKER" (pipeline review B3). The curated view can be empty for a
      // workload the run is full of -- the analyst set the user's own activity aside -- so a
      // bare "no mailbox activity" over a run holding 806 mailbox events is false as written.
      // The row label names the workload, so the note does not: "no mailbox activity tied to the
      // attacker in the records reviewed" wrapped four Brief rows to two lines and overflowed
      // page 1 by ~120px on the first real render.
      why = 'no attacker activity in the records reviewed';
    } else if (unattributed > 0) {
      // 🔴 A genuine "No" -- the source DOES discriminate elsewhere -- but events of this
      // kind were recorded and could not be attributed, so the count must not vanish.
      //
      // Found on the reference case: `other_identities` resolved to a bare "No" while a B2B
      // guest invitation sat in the logs. It is unattributable by construction -- Microsoft
      // ESTS performs the invite, so the recorded addresses are 52.179.80.189 /
      // 40.126.24.153 and there is no session -- but "No" alone asserts the attacker
      // invited nobody, and an executive reading that would never learn a guest account was
      // created during the incident.
      //
      // The STATE follows the plan's workload rule deliberately (deletions on this case are
      // the plan's worked example of a true "No"); the NOTE carries what the state cannot.
      why = `${unattributed} event${unattributed === 1 ? '' : 's'} recorded, `
        + 'none attributable to the attacker';
    }
    rows.push({ id, label, state, count, basisMix: mix, note: why });
  };

  for (const spec of ACTION_SPECS) {
    const mix = { ip: 0, analyst: 0, device: 0, session: 0 };
    let count = 0;
    let unattributed = 0;
    for (const e of events) {
      if (!spec.isCategory(e)) continue;
      const b = attackerBasis(e, attackerCtx);
      if (!b) { unattributed += 1; continue; }
      count += 1;
      mix[b.basis] = (mix[b.basis] || 0) + 1;
    }
    resolve({
      id: spec.id, label: spec.label, workload: spec.workload, count, basisMix: mix, unattributed,
    });
  }

  // Devices are counted per MACHINE, not per event: Entra writes an owner row and a users
  // row for one join, and the finding is how many machines. The rows are already
  // attacker-attributed by construction (deviceRegistration.js), and the basis is `device`
  // -- a registration carries no address and no session, which is why that basis exists.
  const deviceCount = (attackerDeviceRows || []).filter(Boolean).length;
  resolve({
    id: 'devices_joined',
    label: 'Joined devices to the tenant',
    workload: 'directory_audit',
    count: deviceCount,
    basisMix: { ip: 0, analyst: 0, device: deviceCount, session: 0 },
  });

  // Applications are counted per DISTINCT APPLICATION. The set is already attacker-scoped
  // (v1.156.4). There is no per-application basis to report -- an application is not an
  // event -- so the mix is left empty and the invariant is relaxed for this one line only,
  // which the server knows about explicitly.
  const appCount = (attackerApps ? attackerApps.size : 0);
  const appRow = {
    id: 'enterprise_apps',
    label: 'Used applications to reach the account',
    state: appCount > 0 ? 'yes' : 'no',
    count: appCount,
    basisMix: { ip: 0, analyst: 0, device: 0, session: 0 },
    note: appCount > 0 ? '' : 'no application could be attributed to the attacker',
  };
  // Say WHY there is no application, when the records say. A refused sign-in reached nothing;
  // a phishing page recorded in the application field is not an application (schema 1.23).
  if (appCount === 0 && noTokenIssued) {
    const req = (requestedApps || []).filter(Boolean);
    appRow.note = req.length
      ? `no application was reached; sign-in to ${req.join(', ')} was attempted and refused`
      : 'no application was reached; every sign-in attempt was refused';
  } else if (appCount === 0 && (phishingPages || []).length > 0) {
    appRow.note = 'only the phishing page was recorded, which is not an application';
  }
  if (appCount === 0 && failed.has('signin')) {
    appRow.state = 'not_determined';
    appRow.note = 'the sign-in log could not be read for this investigation';
  }
  rows.push(appRow);

  // Stable, meaningful order: how they got the mail, what they did with it, what they left
  // behind. Never sorted by count -- an executive reads the same page shape every time.
  const order = new Map(ATTACKER_ACTION_IDS.map((id, i) => [id, i]));
  rows.sort((a, b) => (order.get(a.id) ?? 99) - (order.get(b.id) ?? 99));
  return rows;
}

function extractMfaChanges(events, tzIana) {
  return events
    .filter((e) => e._eventType === 'DirectoryAudit' && opMatches(e.Operation, MFA_OPS))
    .map((e) => ({
      methodDisplay: e._description || e.Operation,
      timeDisplay: formatFactTime(e.EventTimeUTC, tzIana),
      ip: e.IPAddress || e.InferredIP || '',
      actor: e.UserId || e.InitiatedBy || '',
      operation: e.Operation,
      // v1.189.0: the key the server matches the analyst's attacker marks on. Without it
      // nothing downstream could attribute a row whose own address is Microsoft's.
      eventId: e.EventId || '',
    }));
}

/** Is this row one the analyst marked as attacker activity? */
function isAnalystMarked(evt, marks, sessionMarks = null) {
  // A row on a session the analyst marked as the attacker's (v1.190.0) is marked too.
  if (sessionMarks instanceof Map && sessionMarks.size) {
    const sid = evt?.SessionId || evt?.AADSessionId || evt?.SendSessionId;
    if (sid && sessionMarks.has(sid)) return true;
  }
  return Boolean(analystMarkOf(evt, { analystEvents: marks }));
}

/**
 * Does this row name a machine that only ever signed in from a malicious address?
 *
 * 🔴 This is the ONLY route to attributing a device registration. `initiatedBy` is
 * Microsoft's `Device Registration Service`, the recorded address is EMPTY and there is no
 * session, so every one of the reference case's six attacker device registrations failed
 * both of `selectTimelineEvents`' existing tests and was silently absent from the report —
 * while being persistence that survives a password reset.
 */
function isAttackerDeviceRow(evt, attackerDevices) {
  if (!attackerDevices || !attackerDevices.size) return false;
  return eventDeviceNames(evt).some((d) => attackerDevices.has(d));
}

function selectTimelineEvents(events, maliciousSet, attackerDevices, analystMarks = null, sessionMarks = null) {
  // An executive timeline pins the events that MATTER, not every event. Attacker
  // activity, persistence, and remediation — chronological, capped.
  const significant = events.filter((e) => {
    const ip = e.IPAddress || e.InferredIP;
    if (ip && maliciousSet.has(ip)) return true;
    if (isAnalystMarked(e, analystMarks, sessionMarks)) return true;
    if (INBOX_RULE_OPS.has(e.Operation)) return true;
    if (e._eventType === 'DirectoryAudit' && opMatches(e.Operation, [...MFA_OPS, ...CONSENT_OPS, ...REMEDIATION_OPS])) return true;
    // A device operation, but ONLY when the machine is attributable. An ordinary IT
    // device join during the window is not an executive-timeline event, and admitting
    // every one of them would put routine activity under an attacker heading.
    if (e._eventType === 'DirectoryAudit' && isDeviceOp(e.Operation) && isAttackerDeviceRow(e, attackerDevices)) return true;
    if (e._eventType === 'UrlClick' || e._eventType === 'SecurityAlert') return true;
    return false;
  });
  return significant.sort((a, b) => String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')));
}

function timelineKind(evt, maliciousSet, attackerDevices, analystMarks = null, sessionMarks = null) {
  // Checked FIRST: the mark exists for exactly the row the remediation test below would
  // otherwise colour 'fix' (an attacker's security-info registration on Microsoft's address).
  if (isAnalystMarked(evt, analystMarks, sessionMarks)) return 'attack';
  if (evt._eventType === 'DirectoryAudit' && opMatches(evt.Operation, REMEDIATION_OPS)) {
    const ip = evt.IPAddress || evt.InferredIP;
    // A "remediation" op from a malicious IP is the attacker changing the account,
    // not the responder fixing it — colouring that green would invert the story.
    if (!ip || !maliciousSet.has(ip)) return 'fix';
  }
  const ip = evt.IPAddress || evt.InferredIP;
  if (ip && maliciousSet.has(ip)) return 'attack';
  // A device row has no address of its own, so keying on the malicious set alone would
  // render an admitted attacker registration 'neutral' — the same heading-vs-content
  // mismatch that produced v1.157.0.
  if (evt._eventType === 'DirectoryAudit' && isDeviceOp(evt.Operation) && isAttackerDeviceRow(evt, attackerDevices)) return 'attack';
  return 'neutral';
}

/**
 * The calendar day a row belongs to, in the analyst's display timezone.
 *
 * Stamped once, on the row, because BOTH the digest and the tiered selector group by it
 * and re-deriving it in two places is how two figures about one case come to disagree.
 */
export function stampDigestDay(rows, tzIana) {
  return (rows || []).map((e) => ({
    ...e,
    _digestDay: formatFactTime(e.EventTimeUTC, tzIana, { dateOnly: true }),
  }));
}

/**
 * A COMPLETE per-day digest of attacker-significant activity - the deterministic map.
 *
 * 🔴 THIS IS WHAT MAKES THE ROW CAP SURVIVABLE. The rows handed to the model may be a
 * sample on a very large case; this is never a sample. It is derived in code from the FULL
 * significant set, so every day of activity reaches the model with its counts, its bounds
 * and the kinds of operation it contained, whether or not a row from that day was kept.
 *
 * It is also ~100x cheaper than the rows it summarises: on the reference case the full
 * 667-row array is 130 KB and its digest is ~1.4 KB across 10 days. So the model gets
 * complete coverage of the window for 1% of the payload, and the rows become a matter of
 * detail rather than of what it knows happened at all.
 *
 * Costs ZERO provider calls. A map step done in code is exact, reconcilable against the
 * pinned run blob, and cannot invent a finding - which an LLM asked to categorise batches
 * of rows can, and which this feature's architecture exists to prevent.
 *
 * Counts are strings, like every other number in the pack: the server renders figures and
 * the model must never do arithmetic on them.
 */
export function buildTimelineDigest(significant, kindOf, tzIana) {
  const byDay = new Map();
  for (const e of significant || []) {
    const day = e._digestDay || formatFactTime(e.EventTimeUTC, tzIana, { dateOnly: true });
    if (!day) continue;
    let d = byDay.get(day);
    if (!d) {
      d = { day, attack: 0, fix: 0, neutral: 0, first: '', last: '', ops: new Set() };
      byDay.set(day, d);
    }
    const k = kindOf ? kindOf(e) : 'attack';
    if (k === 'fix') d.fix += 1;
    else if (k === 'neutral') d.neutral += 1;
    else d.attack += 1;
    // `significant` is sorted chronologically by selectTimelineEvents, so first-seen is
    // the day's first event and the last assignment wins for its last.
    const t = formatFactTime(e.EventTimeUTC, tzIana);
    if (!d.first) d.first = t;
    d.last = t;
    const op = String(e.Operation || '');
    if (op) d.ops.add(op);
  }
  return [...byDay.values()].slice(0, CAPS.timelineDigestDays).map((d) => ({
    day: d.day,
    attack: String(d.attack),
    fix: String(d.fix),
    neutral: String(d.neutral),
    firstDisplay: d.first,
    lastDisplay: d.last,
    // Operation names are Microsoft's fixed vocabulary, never analyst or item text.
    operations: [...d.ops].slice(0, 12),
  }));
}

/**
 * Which rows survive when the significant set exceeds one submission.
 *
 * 🔴 NEVER A PREFIX. The old `.slice(0, cap)` took the earliest N, and containment is
 * always LAST - so every case that hit the cap lost its own ending. Tiers, in order:
 *
 *   1. every row that is not plain attacker activity  <- the containment rows live here
 *   2. both ends of the case
 *   3. both ends of every day, so no day of activity can disappear entirely
 *   4. the first occurrence of each distinct operation, so no KIND of action is lost
 *   5. the remainder by even stride, so the sample spans the window instead of
 *      stopping partway through it
 *
 * Returns the kept rows in chronological order. The caller counts what is missing and puts
 * it into `omitted_count`; this function does not mutate the pack.
 */
export function selectTimelineRows(significant, kindOf, cap = CAPS.timeline) {
  const rows = Array.isArray(significant) ? significant : [];
  if (rows.length <= cap) return rows.slice();

  const keep = new Set();
  const add = (i) => {
    if (i < 0 || i >= rows.length) return;
    if (keep.size >= cap && !keep.has(i)) return;
    keep.add(i);
  };

  // 1 - containment and anything else that is not plain attacker activity.
  rows.forEach((e, i) => { if ((kindOf ? kindOf(e) : 'attack') !== 'attack') add(i); });
  // 2 - both ends of the case.
  add(0); add(rows.length - 1);
  // 3 - both ends of each day.
  const firstOfDay = new Map();
  const lastOfDay = new Map();
  rows.forEach((e, i) => {
    const d = String(e._digestDay || '');
    if (!firstOfDay.has(d)) firstOfDay.set(d, i);
    lastOfDay.set(d, i);
  });
  firstOfDay.forEach((i) => add(i));
  lastOfDay.forEach((i) => add(i));
  // 4 - the first of each distinct operation.
  const seenOp = new Set();
  rows.forEach((e, i) => {
    const op = String(e.Operation || '');
    if (op && !seenOp.has(op)) { seenOp.add(op); add(i); }
  });
  // 5 - fill the rest by stride, not head-first.
  if (keep.size < cap) {
    const pool = rows.map((_, i) => i).filter((i) => !keep.has(i));
    const remaining = cap - keep.size;
    if (pool.length <= remaining) {
      pool.forEach((i) => add(i));
    } else {
      const stride = pool.length / remaining;
      for (let k = 0; k < remaining; k += 1) add(pool[Math.floor(k * stride)]);
    }
  }

  return [...keep].sort((a, b) => a - b).map((i) => rows[i]);
}

// ── Size enforcement ────────────────────────────────────────────────────────

/**
 * Byte caps are a coarse FIRST bound only — bytes are not tokens, and the server
 * runs its own token preflight before the provider call. Truncation drops WHOLE
 * records in a fixed priority order (accessed-items tail -> timeline tail -> apps
 * -> folder roll-up), never partial ones, and always reports what went: a silently
 * shortened report is worse than a visibly partial one.
 */
export function enforceFactPackSize(pack, { maxBytes = CAPS.bytes } = {}) {
  let omitted = 0;
  const size = () => JSON.stringify(pack).length;

  // The matrix is ONE ordered budget, not a per-folder loop: it is sorted
  // chronologically and truncation drops the TAIL, so what remains is a contiguous
  // "earliest N" rather than an arbitrary sample of folders.
  const trimAccessedItems = (limit) => {
    const arr = pack.accessed_items || [];
    if (arr.length <= limit) return 0;
    const dropped = arr.length - limit;
    pack.accessed_items = arr.slice(0, limit);
    return dropped;
  };

  omitted += trimAccessedItems(CAPS.accessedItems);

  if (size() > maxBytes) { omitted += trimAccessedItems(400); }
  // 🔴 SAME RULE AS THE ROW CAP: significance, not a prefix (v1.176.0). This path used
  // to `slice(0, 40)`, so a pack that overflowed on BYTES lost its containment rows even
  // when the row cap had not been reached. The rows here are already mapped, so `kind` is
  // read off the row rather than recomputed.
  if (size() > maxBytes && (pack.timeline || []).length > 40) {
    const before = pack.timeline.length;
    pack.timeline = selectTimelineRows(pack.timeline, (r) => r.kind, 40);
    omitted += before - pack.timeline.length;
  }
  if (size() > maxBytes && (pack.apps || []).length > 20) {
    omitted += pack.apps.length - 20;
    pack.apps = pack.apps.slice(0, 20);
  }
  if (size() > maxBytes && (pack.mailbox_folders || []).length > 15) {
    omitted += pack.mailbox_folders.length - 15;
    pack.mailbox_folders = pack.mailbox_folders.slice(0, 15);
  }

  pack.omitted_count = (pack.omitted_count || 0) + omitted;
  pack.facts_truncated = pack.omitted_count > 0;
  return pack;
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * How was each application actually recorded? (schema 1.11)
 *
 * 🔴 A NAME IN THIS LIST IS NOT A CLAIM THAT THE APPLICATION WAS REACHED ON ITS OWN.
 * The non-interactive feed arrives PRE-AGGREGATED at (session, IP, hour) grain, and one
 * row names every application seen in that bucket -- measured on the reference case, one
 * row lists 8 names against its own AppCount of 9. So an application can appear here
 * solely because it shares a bucket with another, and Addendum C was printing it as a row
 * of its own, indistinguishable from an application the attacker signed into directly.
 * `M365ChatClient` and `Dime Client` are that case's examples: the analyst could not
 * corroborate either anywhere in the case log, because there is nothing to corroborate --
 * neither was ever recorded alone.
 *
 * Two things are therefore derived here, and NOTHING else changes about the set (page 1's
 * `enterprise_apps` count is the same set, so the two cannot disagree):
 *
 *   attested  the application was named ON ITS OWN at least once -- an interactive
 *             sign-in names exactly one application, and a non-interactive bucket whose
 *             own AppCount is 1 names exactly one too.
 *   group     a positive integer shared by applications that have NO independent
 *             attestation and only ever appear inside the same aggregated buckets. They
 *             are indistinguishable at this grain, so they render as ONE row.
 *
 * ⚠️ Grouping is by CONNECTED COMPONENT, not per bucket: if one bucket pairs A with B and
 * another pairs B with C, then A, B and C are mutually indistinguishable and belong in one
 * group. Splitting them per bucket would print B twice and imply two separate findings.
 *
 * ⚠️ An unattested application in a component of ONE is not a group -- it gets no group
 * number and its own row. It still carries `attested: false`, because "only ever seen
 * alongside others" is true of it. On the reference case that is `Windows Sign In`: five
 * buckets, never alone, but every co-member independently attested.
 */
export function buildAppAttribution({ signInEvents, nonInteractiveEvents, maliciousSet }) {
  const solo = new Set();
  for (const e of signInEvents || []) {
    if (!maliciousSet.has(e?.IPAddress)) continue;
    const a = e.ApplicationName || e._appName;
    if (a && a !== 'Unknown') solo.add(a);
  }

  const bundles = [];
  let truncated = false;
  for (const e of nonInteractiveEvents || []) {
    if (!maliciousSet.has(e?.IPAddress)) continue;
    const apps = String(e.Apps || '').split(',').map((x) => x.trim())
      .filter((x) => x && x !== 'Unknown');
    if (!apps.length) continue;
    // AppCount is the feed's OWN distinct total and Apps is capped, so a bucket can name
    // fewer than it counted. Disclosed rather than silently short.
    const total = Number(e.AppCount) || apps.length;
    if (total > apps.length) truncated = true;
    // A bucket of one attests that application by itself -- but only if its own count
    // agrees, or the single name is just the head of a truncated list.
    if (apps.length === 1 && total <= 1) solo.add(apps[0]);
    else bundles.push(apps);
  }

  // Union-find over the applications with no independent attestation.
  const parent = new Map();
  const add = (x) => { if (!parent.has(x)) parent.set(x, x); };
  const find = (x) => {
    let r = x;
    while (parent.get(r) !== r) { parent.set(r, parent.get(parent.get(r))); r = parent.get(r); }
    return r;
  };
  const union = (a, b) => {
    add(a); add(b);
    const ra = find(a); const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const b of bundles) {
    const rest = b.filter((a) => !solo.has(a));
    rest.forEach(add);
    for (let i = 1; i < rest.length; i += 1) union(rest[0], rest[i]);
  }

  const members = new Map();
  for (const a of parent.keys()) {
    const r = find(a);
    if (!members.has(r)) members.set(r, []);
    members.get(r).push(a);
  }
  // Numbered by each component's alphabetically-first member, so the number is stable
  // across builds rather than depending on Map insertion order.
  const groups = [...members.values()].filter((c) => c.length > 1)
    .map((c) => c.slice().sort((x, y) => x.localeCompare(y)))
    .sort((x, y) => x[0].localeCompare(y[0]));
  const groupOf = new Map();
  groups.forEach((c, i) => c.forEach((a) => groupOf.set(a, i + 1)));

  return { attested: solo, groupOf, groupCount: groups.length, truncated };
}

/**
 * Build the fact pack from the EXACT params object handleClientReport already
 * passes, plus the resolved timezone and the source run id.
 *
 * includeLogBreakdown / hideMsSubstrate / hideOutlookNoise are easy to overlook
 * because they are not data — but they are filter flags that change what the
 * report SAYS, so all three ride in filter_snapshot and are rendered in the
 * report's provenance block. Without that, two reports from the same run can
 * legitimately differ with no recorded reason.
 */
export function buildExecReportFactPack(params) {
  const {
    caseData, results, filteredResults, attackerWindow,
    ipClassifications = {}, ipComments = {}, urlClassifications = {},
    effectiveSafeIPSet, tenantSafeIPs, knownApps,
    selectedTz, tzIana, tzLabel,
    includeLogBreakdown = false, hideMsSubstrate = false, hideOutlookNoise = false,
    maskSubjects = true,
    dismissedEventIds = [],
    // The analyst's "attacker activity" marks (v1.189.0): Map<eventId,{reason,by,at}> or
    // the case field's object form. The SERVER rebuilds its own copy from the Cases entity
    // for every claim it renders; this one drives the checklist and metrics, which are
    // computed here like every other attribution.
    attackerEventIds = null,
    // Sessions the analyst marked as the attacker's (utils/sessionMarks.js, v1.190.0):
    // Map<sessionId,{reason,by,at}>. Same footing as the row marks above; the server rebuilds
    // its own copy from the Cases entity (`analyst_sessions`).
    sessionMarks = null,
    runId = '',
    appVersion = '',
    // All fourteen filter-bar toggles. Only three used to ride along, so the other
    // eleven never left the browser and the server had no way to verify the view a
    // report was built from — see execReportFilterProfile.js.
    filterState = null,
    // 🔴 `reviewedEventCount` / `excludedEventCount` USED TO BE PARAMS and are now derived
    // here (v1.171.0). CaseDetail summed the fourteen filtered workload ARRAYS, which counts
    // records no view of the case can display: on IR-20260826-gwoodward-001 that reported
    // "167 events were incident related" over a screen showing 43 rows, of which 83 were
    // ordinary inbound mail (the timeline shows message trace only when it is outbound or
    // flagged as the phishing entry point) and 8 were URL clicks the analyst had already
    // ruled out by confirming a different one.
    //
    // A pill is how an analyst says "not the attacker's story". A record the timeline never
    // renders is outside that instrument -- it can be neither eliminated nor affirmed -- so
    // counting it as incident-related asserts a judgment nobody made. The count is therefore
    // taken from the stream the analyst's final view is built from, which is the same stream
    // every other figure in this report already comes from.
  } = params;

  const fr = filteredResults?.filtered || filteredResults || {};
  // Both feed the SHARED selection rules in combineAndTagEvents. `phishingFlags` is a JSON
  // STRING on a normalized case (default '[]'), which is why it goes through asArray -- a
  // bare `.map()` here threw on every report once already.
  const phishFlags = asArray(caseData?.phishingFlags);
  const ipLookup = buildIpLookup(fr.IPAddresses || results?.IPAddresses || []);
  const knownAppIdSet = new Set();
  (knownApps || []).forEach((ka) => knownAppIdSet.add(ka.appId));

  const events = combineAndTagEvents({
    filteredResults: fr,
    ipLookup,
    knownAppIdSet,
    hideKnownApps: true,
    hideMsSubstrate,
    hideOutlookNoise,
    ipClassifications,
    phishingFlags: phishFlags,
    urlClassifications,
    targetUpn: caseData?.userPrincipalName,
    maskSubjects,
  });

  const maliciousSet = new Set(attackerWindow?.maliciousIPs || []);
  // Machines only ever seen from a malicious address. Shares ONE implementation with the
  // timeline badge (utils/attackerAttribution.js) so the report and the screen cannot
  // disagree about which machines are the attacker's, and is fed THIS pack's own malicious
  // set rather than re-deriving it from the classifications.
  // ONE context, shared with the timeline badge's implementation, fed THIS pack's own
  // malicious set. `attackerDevices` comes off it rather than from a second
  // buildAttackerDevices call — same arguments, same value, one derivation.
  const analystMarks = attackerEventIds instanceof Map ? attackerEventIds : parseAttackerEventMarks(attackerEventIds);
  const sessMarks = sessionMarks instanceof Map ? sessionMarks : new Map();
  const attackerCtx = buildAttackerContextFrom(fr, maliciousSet, analystMarks, sessMarks);
  const attackerDevices = attackerCtx.attackerDevices;
  // Identity-keyed companion to the name-keyed set above, from the same rows. Needed for the
  // device fallback below; a pre-runbook blob carries no GUIDs, so `ids` is empty and the
  // fallback contributes nothing -- the report then behaves exactly as it did before.
  const attackerDeviceIndex = buildAttackerDeviceIndex(fr, maliciousSet);
  const phishingSubjects = new Set(
    asArray(caseData?.phishingFlags).map((p) => String(p?.subject || '').toLowerCase()).filter(Boolean),
  );

  // ── Counts ────────────────────────────────────────────────────────────
  const signIns = events.filter((e) => e._eventType === 'SignIn');
  // Malicious-address sign-ins PLUS the window's ANCHORING legs: `successfulAttackerSignInCount`
  // counts them, so `attacker_signins` must too or the two metrics disagree on the same page.
  // 🔴 Read off the window, never re-derived by shape (v1.188.0): `isPinnedSignInEvent` also
  // matches Microsoft's ordinary MFA sub-steps, and counting it here printed 61 "attacker
  // sign-ins" for 9 relay legs plus 52 of the victim's own (IR-20260930-mendozap-001). On that
  // case the figure is 20 = 11 successful + 9 failed; the relay legs are INSIDE the 11.
  const anchoringLegIds = attackerWindow?.anchoringLegIds || new Set();
  // v1.190.0: a sign-in on a session the analyst marked as the attacker's is the attacker's
  // too -- the window counts it (it issued a token), so this metric must as well.
  const attackerSignIns = signIns.filter((e) => maliciousSet.has(e.IPAddress) || anchoringLegIds.has(e.EventId)
    || (e.SessionId && sessMarks.has(e.SessionId)));
  // Capture (URL-shaped application) and a recovered MFA leg are different claims — the label
  // says "phishing-kit landing" only for the first.
  const hasKitLegs = (attackerWindow?.captureLegIds?.size || 0) > 0;
  const hasRecoveredAnchors = (attackerWindow?.recoveredLegAnchorCount || 0) > 0;
  // 🔴 The app set spans BOTH feeds. Counting interactive sign-ins alone reported
  // "4 Applications used in the attacker session" in a report whose own prose named SIX
  // (it reads the timeline, which includes the non-interactive feed) — a stat card
  // contradicting the document it sits in. Truth on the reference case: 7 interactive, 15
  // including non-interactive. This metric is NOT one the server ceilings against a
  // per-workload total, so widening it is safe; `attacker_signins` and the other ceilinged
  // metrics stay on the interactive stream, where reconciliation requires them.
  // 🔴 ONLY SIGN-INS THAT ISSUED A TOKEN REACHED AN APPLICATION (schema 1.23). A refused
  // attempt names the application it ASKED for (Office 365 on every refused Login:login row of
  // IR-20261013-levig-001); counting that as "used applications to reach the account" is the
  // same false access claim the review found in the window. Those names are carried instead as
  // `attacker_window.requestedApps` and stated as attempted-and-refused.
  const refusedKeys = refusedAttemptKeys(asArray(results?.SignInEvents || fr.SignInEvents));
  const appNameOf = (e) => e.ApplicationName || e._appName || '';
  const attackerApps = new Set(
    attackerSignIns
      .filter((e) => isTokenIssuingSignIn(e, refusedKeys) || isUrlShapedApp(appNameOf(e) || e.ApplicationId))
      .map((e) => appNameOf(e) || (isUrlShapedApp(e.ApplicationId) ? e.ApplicationId : ''))
      .filter(Boolean),
  );
  for (const evt of asArray(fr.NonInteractiveSignInEvents)) {
    const ip = evt?.IPAddress;
    if (!ip || !maliciousSet.has(ip)) continue;
    for (const a of String(evt.Apps || '').split(',')) {
      const t = a.trim();
      if (t) attackerApps.add(t);
    }
  }
  // 'Unknown' is the timeline column's placeholder for an event with no application. It is
  // not an application and must not be counted as one or listed as one.
  attackerApps.delete('Unknown');
  // 🔴 A URL IS NOT AN APPLICATION (schema 1.23). An attacker-in-the-middle kit's MFA-step rows
  // record the kit's own page where the application id belongs; that is the campaign's
  // signature, and printing it under "Used applications to reach the account: Yes" told a
  // client the attacker had used an app to get in (IR-20261013-levig-001, pipeline review B1).
  // Such names move to `phishing_pages` and are never counted as applications.
  const phishingPages = [...attackerApps].filter(isUrlShapedApp).sort();
  for (const p of phishingPages) attackerApps.delete(p);

  // Authentications the attacker's addresses made through the non-interactive feed. Summed
  // from EventCount because a row is a bucket, not an event -- see the metric below.
  let attackerTokenUse = 0;
  for (const evt of asArray(fr.NonInteractiveSignInEvents)) {
    if (!evt?.IPAddress || !maliciousSet.has(evt.IPAddress)) continue;
    const n = Number(evt.EventCount);
    attackerTokenUse += Number.isFinite(n) && n > 0 ? n : 1;
  }

  // 🔴 THE MAIL FIGURES COUNT ATTACKER-ATTRIBUTED ACTIVITY ONLY (fact schema 1.26, 2026-10-28).
  // They counted the whole curated view, so a Report printed "Distinct emails recorded as opened:
  // 182" beside a checklist answering Read mail = No (IR-20260730-shicks1-001, none of its 185
  // rows attributable), and handed the model the same number. Same test as the checklist
  // (`attackerBasis`), so the figure and the line beneath it count the same events. The fifth
  // instance of "a count is not an attribution", after apps, sign-in methods, files and devices.
  const attributedEvents = events.filter((e) => attackerBasis(e, attackerCtx));
  const mailbox = extractMailboxDetail(attributedEvents);
  const accessed = extractAccessedItems(events, {
    maskSubjects, phishingSubjects, tzIana, maliciousSet, attackerCtx,
  });
  const phishEntry = extractPhishingEntryPoint({
    caseData, results, filteredResults: fr, urlClassifications, maskSubjects, tzIana,
    attackerWindow,
  });
  const inboxRules = extractInboxRules(events, tzIana).slice(0, CAPS.inboxRules);
  const mfaChanges = extractMfaChanges(events, tzIana).slice(0, CAPS.mfaChanges);
  const consents = attributedEvents.filter((e) => e._eventType === 'DirectoryAudit' && opMatches(e.Operation, CONSENT_OPS));
  // Inbox rules: the pack keeps every rule (the server's rules table decides attribution per
  // rule), but the FIGURE counts only the attacker's, like every other attacker-facing count.
  const attackerRuleCount = extractInboxRules(attributedEvents, tzIana).length;

  // Read the RAW trace array, not the tagged stream: combineAndTagEvents keeps only
  // `Direction === 'Sent'` (eventUtils.js), so `_eventType === 'MessageTrace'` is
  // already outbound-only. Counting "total traced" off it would report sent mail as
  // the whole traced period and make `inbound` below a subtraction of one set from
  // itself.
  const messageTrace = fr.MessageTraceEvents || results?.MessageTraceEvents || [];
  // Matched against EVERY address the mailbox answers to, not just the case UPN —
  // see mailboxAddressSet.
  const mailboxAddresses = mailboxAddressSet(caseData, results);
  const outbound = messageTrace.filter((e) => {
    const from = String(e.SenderAddress || e.UserId || '').trim().toLowerCase();
    return from && mailboxAddresses.has(from);
  });
  // The attacker's sends: the checklist's own sent_mail rows (outbound message trace in the
  // curated stream, attributed). "Emails sent 1 / 112" over Sent mail = No was the user's own
  // mail (IR-20260624-neilgarver-001).
  const attackerSends = attributedEvents.filter((e) => e._eventType === 'MessageTrace').length;

  // 🔴 ATTRIBUTION-GATED, and they were not.
  //
  // These two were plain presence counts over every SharePoint row in the curated view.
  // On IR-20260818-Controller-001 that is 1,261 rows of which 1,175 come from
  // 24.142.160.98 — the address the analyst classified SAFE, on the real machine
  // `99731-1` — and NOT ONE row is attributable to the attacker by address, device or
  // session. The report nonetheless printed "SharePoint / OneDrive items opened: 254"
  // and "Files downloaded: 98" under attacker-facing headings: the real user's own file
  // activity, presented as the intruder's.
  //
  // Third instance of one defect shape (v1.156.4 `apps[]`, v1.157.0 `mfa_changes`), so
  // the rule is now explicit: A COUNT IS NOT AN ATTRIBUTION. Any basis qualifies here —
  // a session-only file open is still worth reporting — but the LABEL says "attributed"
  // rather than naming the attacker outright, because `session` is corroboration and not
  // proof and a stat card has no room for the distinction.
  //
  // The operation sets are now the SAME ones extractAccessedItems uses. They were a
  // narrower inline list (no FileAccessedExtended), so the stat card could disagree with
  // the addendum below it about the same events.
  const sharePoint = events.filter((e) => e._eventType === 'SharePoint' || e._eventType === 'SharePointList');
  const attackerSharePoint = sharePoint.filter((e) => attackerBasis(e, attackerCtx));
  const filesAccessed = attackerSharePoint.filter((e) => FILE_READ_OPS.has(e.Operation));
  const filesDownloaded = attackerSharePoint.filter((e) => FILE_DOWNLOAD_OPS.has(e.Operation));

  const confirmedClicks = Object.values(urlClassifications).filter((v) => v === 'confirmed').length;

  // Canonical log_type names, NOT raw _eventType tags — see LOG_TYPE_BY_EVENT_TYPE.
  //
  // The non-interactive feed is added explicitly rather than read off a stream, because
  // it reaches the pack only through the rendered timeline (declared below) and this list
  // must not depend on declaration order. It is listed when the feed CARRIED rows, which
  // is what "reviewed" means here — the analyst's view included it.
  const logTypesReviewed = [...new Set(
    events.map((e) => LOG_TYPE_BY_EVENT_TYPE[e._eventType]).filter(Boolean),
  )];
  if ((fr.NonInteractiveSignInEvents || []).length > 0) {
    logTypesReviewed.push(LOG_TYPE_BY_EVENT_TYPE.NonInteractiveSignIn);
  }

  // ── Metrics: display strings the SERVER renders into stat cards ────────
  const metrics = {};
  const metric = (id, value, label) => {
    if (!METRIC_IDS.includes(id)) return;              // an unknown id is rejected server-side
    if (value === null || value === undefined || value === '') return;
    metrics[id] = { value: String(value), label };
  };

  // LABELS DESCRIBE A REVIEWED VIEW, NOT THE WHOLE PERIOD. Every count here is computed
  // from the analyst's curated view, which is correct — that view is what they sign off
  // on. But an absolute-sounding label sitting on a curated number overclaims:
  // `message_trace_total` was labelled "Messages in the traced period" while counting 93
  // of the run's 210. So the wording says "reviewed" wherever the basis is the curated
  // view, and no label may imply completeness the number cannot support. The SERVER now
  // renders these labels and ignores the model's (Format-ExecReportStats) — otherwise the
  // model could put "Messages in the traced period" straight back on top of this number.
  // 🔴 WITH NO TOKEN ISSUED, THE DATES ARE THE ATTEMPTS (schema 1.25). The attacker window is
  // built from "successful" rows, and for a refused relay those are only the MFA-wait steps,
  // so the page ended a minute before the last refused attempt (IR-20261021-ehribik-001:
  // "12:52–12:57" against a last attempt at 12:58:05). The heading already says "when the
  // sign-in attempts were made", so the span must cover every attempt.
  const attemptSpan = attemptWindowSpan(attackerWindow);
  const windowDuration = formatDurationText(attemptSpan.first, attemptSpan.last);
  // A refused attempt is not "hands-on activity" (schema 1.23).
  metric('attacker_window_duration', windowDuration,
    attackerWindow?.noTokenIssued ? 'Period of refused sign-in attempts' : 'Hands-on attacker activity');
  // "Interactive" is load-bearing in both labels as of v1.172.0: the non-interactive
  // authentications are counted separately below, and a bare "sign-ins" over the
  // interactive-only figure reads as the total.
  metric('attacker_signins', attackerSignIns.length,
    // With every attempt refused these rows are attempts and their MFA-wait steps, not
    // sign-ins a reader should count as access (pipeline review of IR-20261021-ehribik-001).
    attackerWindow?.noTokenIssued ? 'Attacker sign-in records (refused attempts and their MFA steps)'
      : hasKitLegs ? 'Interactive attacker sign-ins (incl. phishing-kit landing)'
      : hasRecoveredAnchors ? 'Interactive attacker sign-ins (incl. MFA steps linked to the attacker’s sign-in transactions)'
        : 'Interactive sign-ins from malicious addresses');
  // A refused attempt's MFA-step rows log "Success"; with no token issued, none of them was a
  // successful sign-in (schema 1.23).
  metric('successful_attacker_signins',
    attackerWindow?.noTokenIssued ? 0 : attackerWindow?.successfulAttackerSignInCount,
    'Successful interactive sign-ins');
  // 🔴 AUTHENTICATIONS, NOT ROWS -- and the server ceilings it against the summed
  // EventCount for exactly this reason. A row is an aggregate over (session, IP, hour), so
  // the reference case's 46 attacker authentications are ONE blob row: reporting 46 against
  // a row-count ceiling of 1 would 422 a correct pack, and reporting 1 would understate the
  // finding by a factor of 46. Emitted only when non-zero, so a case without the feed is
  // unchanged.
  if (attackerTokenUse > 0) {
    metric('attacker_token_use', attackerTokenUse, 'Token authentications from malicious addresses');
  }
  metric('malicious_ip_count', (attackerWindow?.maliciousIPs || []).length, 'Malicious IP addresses');
  metric('distinct_apps_used', attackerApps.size, 'Applications used in the attacker session');
  // The next four are attacker-attributed since 1.26 (see `attributedEvents`); the labels say so.
  metric('mailbox_items_accessed', mailbox.accessEvents, 'Mailbox access events in attacker-attributed activity');
  // 🔴 The label says what the metric MEASURES, not what it implies. It counts messages
  // with a RECORDED OPEN; it does not bound what was exposed. On the reference case it is
  // 0 while the attacker held full mailbox control, and "Distinct emails the attacker
  // could see: 0" in a client-facing report reads as "no mail was exposed" — a
  // breach-notification-relevant claim this figure cannot support.
  metric('distinct_messages_read', mailbox.distinctMessages, 'Distinct emails recorded as opened in attacker-attributed activity');
  metric('mailbox_folders_touched', mailbox.folders.length, 'Mail folders touched in attacker-attributed activity');
  metric('inbox_rules_created', attackerRuleCount, 'Inbox rules created or changed in attacker-attributed activity');
  // 🔴 ATTRIBUTION-GATED, AND IT WAS NOT -- FOURTH INSTANCE OF THIS DEFECT SHAPE, after
  // v1.156.4 (`apps[]`), v1.157.0 (`mfa_changes` in the RENDERER) and v1.159.0 (files).
  //
  // This was `mfaChanges.length`: every sign-in-method event in the curated view, with no
  // attribution test whatever. On IR-20260818-Controller-001 that is 8 events and ALL EIGHT
  // ARE THE DEFENDERS' REMEDIATION -- the Aug 3 password reset by "Microsoft password reset
  // service" and the Aug 18 re-registration of the real user's authenticator. The checklist
  // says, correctly, "Added or changed sign-in methods: No -- 8 events recorded, none
  // attributable to the attacker", and the model was handed "8" under the label "Sign-in
  // methods registered or changed" and duly wrote that the attacker altered the account's
  // sign-in security information. A client-facing document attributed the containment team's
  // own work to the attacker, contradicting its own table on the same page.
  //
  // v1.157.0 gated the RENDERER for exactly this reason; the metric and the array were left
  // ungated. The label moves with the gate (the v1.159.0 rule): a gated number under an
  // ungated label is just a new wrong claim.
  const mfaAttributed = events.filter((e) => (
    e._eventType === 'DirectoryAudit' && opMatches(e.Operation, MFA_OPS) && attackerBasis(e, attackerCtx)
  )).length;
  metric('mfa_methods_changed', mfaAttributed, 'Sign-in methods changed in attacker-attributed activity');
  metric('apps_consented', consents.length, 'Third-party apps granted access in attacker-attributed activity');
  metric('message_trace_total', messageTrace.length, 'Messages reviewed');
  metric('files_accessed', filesAccessed.length, 'Files opened in attacker-attributed activity');
  metric('files_downloaded', filesDownloaded.length, 'Files downloaded in attacker-attributed activity');
  metric('confirmed_phish_clicks', confirmedClicks, 'Confirmed malicious link clicks');
  metric('log_types_reviewed_count', logTypesReviewed.length, 'Log sources reviewed');
  // "0 / 44" reads far better on a stat card than a bare 0 — the server treats
  // metric values as opaque display strings, and reconciliation skips non-integers.
  if (messageTrace.length > 0) {
    metric('emails_sent', `${attackerSends} / ${messageTrace.length}`, 'Emails sent in attacker-attributed activity, of messages reviewed');
  } else {
    metric('emails_sent', attackerSends, 'Emails sent in attacker-attributed activity');
  }

  // ── Timeline: ids the model references instead of restating times ──────
  //
  // 🔴 Built from a SEPARATE stream, rolled and including the non-interactive feed, so
  // the executive timeline shows what the analyst signed off on in All Events. `events`
  // above stays per-event and non-interactive-free ON PURPOSE: every metric, the accessed-
  // items matrix and the mailbox detail derive from it, and the server bounds each metric
  // by the pinned run blob's own per-workload totals -- a rolled stream would report 6
  // sign-ins where the run holds 620 and fail reconciliation. Counting and rendering are
  // different surfaces; see utils/allEventsRollups.js.
  //
  // 🔴 COUNTED BEFORE IT IS ROLLED, and that is the point of splitting the variable out
  // (v1.171.0). `renderStream` is the RECORDS the analyst's final view contains;
  // `timelineEvents` is the ROWS it draws, after sign-ins become access periods and
  // directory records become actions. Both numbers are true and they are not the same
  // number, so the report states each as what it is: a record count is the finding (23
  // attacker sign-ins), a row count is what a reader sees on screen (43 rows), and
  // reporting one under the other's name is how "167" came to sit over a 43-row table.
  const renderStream = combineAndTagEvents({
    filteredResults: fr,
    ipLookup,
    knownAppIdSet,
    hideKnownApps: true,
    hideMsSubstrate,
    hideOutlookNoise,
    ipClassifications,
    phishingFlags: phishFlags,
    urlClassifications,
    targetUpn: caseData?.userPrincipalName,
    maskSubjects,
    includeNonInteractive: true,
  });
  // 🔴 COLLAPSE MESSAGE TRACE BEFORE ROLLING, EXACTLY WHERE TimelineView DOES (v1.175.0).
  // Message trace is stored PER RECIPIENT, so one send to three addresses is three records
  // and ONE row. TimelineView collapses in its phase 1; `storyGenerator` collapses via
  // collapseMessageTraceInStream; **this pack did neither**, so its row count ran ahead of
  // the screen by the fan-out -- 483 against 482 on IR-20260818-Controller-001, which is the
  // exact equality v1.171.0 shipped. (allEventsRollups' header claimed "both sides already
  // do it in their own phase 1 ... and they already agree"; that was true of storyGenerator
  // and false here.)
  //
  // 🔴 ROWS ONLY, NEVER THE RECORD COUNT. `reviewedEventCount` above stays on the
  // uncollapsed stream: collapse is a DISPLAY grouping, like an access period, and the
  // scope sentence already draws that distinction. Counting collapsed records would also
  // corrupt `excludedEventCount` (raw minus reviewed), which would then describe merged
  // recipient rows as "normal activity, set aside by the reviewing analyst" -- they were
  // neither normal nor set aside, they were merged.
  const timelineEvents = applyAllEventsRollups(collapseMessageTraceInStream(renderStream));

  // Records in the analyst's final view, the rows it draws, and everything the view removed.
  //
  // `excluded` is raw MINUS reviewed rather than a sum of per-toggle counts: an event that is
  // both post-window and failed is counted by two of those, and a disclosure that overstates
  // is no better than one that understates.
  const reviewedEventCount = renderStream.length;
  const reviewedRowCount = timelineEvents.length;
  const rawEventTotal = RESULT_ARRAYS.reduce(
    (n, k) => n + (Array.isArray(results?.[k]) ? results[k].length : 0), 0,
  );
  const excludedEventCount = Math.max(0, rawEventTotal - reviewedEventCount);

  // 🔴 THE ROWS ARE A SAMPLE ON A HUGE CASE; THE DIGEST NEVER IS (v1.176.0).
  //
  // What used to happen here was `.slice(0, 150)` -- the earliest 150 significant rows,
  // with nothing counted and nothing said. Containment is always LAST, so every case that
  // reached the cap lost its own ending: on IR-20260818-Controller-001 the model was given
  // Jul 9 - Jul 29 of a window closing Aug 3 and not one remediation row.
  //
  // Three things now hold, in this order of importance:
  //   1. `timeline_digest` is built from the FULL significant set, so no day of activity is
  //      invisible to the model regardless of what the row cap does. It is the map step,
  //      done in code -- exact, reconcilable, and free of provider calls.
  //   2. `selectTimelineRows` picks by significance, so the containment rows, both ends of
  //      the case, both ends of every day and the first of each operation survive first.
  //   3. Whatever it still drops is COUNTED into `omitted_count` below, so a truncation can
  //      no longer be silent -- and the server's own over-cap check stops being unreachable.
  const kindOfRow = (e) => timelineKind(e, maliciousSet, attackerDevices, analystMarks, sessMarks);
  const significantRows = stampDigestDay(
    selectTimelineEvents(timelineEvents, maliciousSet, attackerDevices, analystMarks, sessMarks), tzIana,
  );
  const timelineDigest = buildTimelineDigest(significantRows, kindOfRow, tzIana);
  const keptRows = selectTimelineRows(significantRows, kindOfRow, CAPS.timeline);
  const timelineOmitted = Math.max(0, significantRows.length - keptRows.length);
  const timeline = keptRows.map((e, i) => ({
      id: `t${i + 1}`,
      timeDisplay: formatFactTime(e.EventTimeUTC, tzIana),
      description: e._description || e.Operation || '',
      eventType: e._eventType || '',
      operation: e.Operation || '',
      ip: e.IPAddress || e.InferredIP || '',
      kind: kindOfRow(e),
    }));

  // ── Devices the attacker joined to the tenant ─────────────────────────
  //
  // 🔴 The strongest remediation finding a case of this shape carries, and it was absent
  // from every report generated before v1.158.0. Joining a device grants a Primary Refresh
  // Token, so it is persistence that a password reset does NOT clear — on the reference
  // case the report stated that no sign-in method had been registered from a malicious
  // address (true) while six attacker machines sat in the directory unmentioned.
  //
  // Rendered by the SERVER (`Format-ExecReportAttackerDevices`) into the persistence
  // section, and STRIPPED before the provider call: the model contributes no cell, no
  // ordering and no device name. It gets the count as a metric so it can refer to the
  // finding without restating it.
  // 🔴 TWO SOURCES, IN THIS ORDER, AND THE ORDER IS THE POINT.
  //
  // PRIMARY is the directory-audit registration event: Microsoft recorded the act, named the
  // machine, and the event's own attribution decides whose it was. Nothing beats that.
  //
  // FALLBACK is identity from sign-in history, and it exists because the primary source is
  // SILENT when the feed was not collecting. On IR-20260818-Controller-001, directory-audit
  // collection begins 2026-07-20 and DESKTOP-D61DA9 was registered 2026-07-09 -- so the
  // report named six machines and omitted the seventh, which is the INITIAL-ACCESS device and
  // was still enabled 22 days after containment. A device joined outside the collected window
  // is not a device that was not joined.
  //
  // The fallback qualifies a machine only if every sign-in that carries its GUID came from an
  // analyst-classified malicious address and none came from anywhere else -- the same
  // `device`-basis rule the timeline badge uses, keyed on identity rather than on a hostname.
  const regRows = asArray(timelineEvents).filter((e) => e._eventType === 'DirectoryAudit'
    && isDeviceRegistrationOp(e.Operation)
    && isAttackerDeviceRow(e, attackerDevices));
  const seenDeviceIds = new Set();
  const namedByRegistration = new Set();
  const attackerDeviceFacts = [];
  for (const e of regRows.sort((a, b) => String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')))) {
    const device = dirAuditDeviceName(e);
    const objectId = dirAuditDeviceObjectId(e);
    // One row per DEVICE. Microsoft writes an owner row and a users row for the same
    // join, and the analyst's finding is a count of machines, not of audit records.
    const key = (objectId || device).toLowerCase();
    if (!device || seenDeviceIds.has(key)) continue;
    seenDeviceIds.add(key);
    namedByRegistration.add(device.toLowerCase());
    if (attackerDeviceFacts.length >= CAPS.attackerDevices) break;
    attackerDeviceFacts.push({
      device,
      objectId,
      timeDisplay: formatFactTime(e.EventTimeUTC, tzIana),
      operation: e.Operation || '',
      registrationSeen: true,
    });
  }

  // ── Fallback: attacker machines with no registration event in the reviewed period ──
  //
  // 🔴 AMBIGUITY IS DECLINED, NOT GUESSED. Linking a GUID to a name needs the case's own
  // name<->id map, because the registration event carries the Entra OBJECT id (a different
  // GUID for the same machine) and cannot be joined to a sign-in directly. Where one name
  // maps to several GUIDs -- a rename, or two machines sharing a hostname -- the link is not
  // determinable from the case data, and picking one would attribute one machine's activity
  // to another. Such a device is omitted rather than asserted: a missing row understates,
  // a wrong row misdirects remediation.
  const fallbackIds = [...(attackerDeviceIndex.ids || [])]
    .filter((id) => {
      const nm = attackerDeviceIndex.nameById.get(id);
      if (!nm) return false;                                   // no determinable name
      if (attackerDeviceIndex.ambiguousNames.has(nm)) return false;
      return !namedByRegistration.has(nm);                     // primary already has it
    })
    .sort();
  for (const id of fallbackIds) {
    if (attackerDeviceFacts.length >= CAPS.attackerDevices) break;
    const lower = attackerDeviceIndex.nameById.get(id);
    // Earliest attacker-attributed sighting. NOT called a registration time: the
    // registration itself is outside the reviewed period and its timestamp is not in this
    // run, so naming it one would state a fact the pack cannot support. `registrationSeen`
    // false is what tells the renderer to say so.
    let firstSeen = '';
    let label = lower;
    for (const key of ['SignInEvents', 'NonInteractiveSignInEvents']) {
      for (const e of asArray(fr?.[key])) {
        if (!eventDeviceIds(e).includes(id)) continue;
        const ts = String(e.EventTimeUTC || '');
        if (ts && (!firstSeen || ts < firstSeen)) firstSeen = ts;
        const shown = eventDeviceLabel(e, lower);
        if (shown) label = shown;
      }
    }
    attackerDeviceFacts.push({
      device: label,
      objectId: '',
      timeDisplay: firstSeen ? formatFactTime(firstSeen, tzIana) : '',
      operation: '',
      registrationSeen: false,
    });
  }
  if (attackerDeviceFacts.length > 0) {
    metric('attacker_devices_registered', attackerDeviceFacts.length,
      'Attacker devices joined to the tenant');
  }

  // ── The Executive Brief's page-1 checklist ────────────────────────────
  // Built here rather than in the Brief's own builder because there is ONE fact pack and
  // ONE schema version (plan section 5.7): a second builder is this feature's established
  // bug factory. The Report ignores this key; the Brief renders it.
  //
  // Coverage comes from the RUN's own `dataSourceErrors`, not from anything asserted here —
  // a workload whose query failed must read as "not determined", never as "nothing
  // happened". The server re-derives the same thing from the PINNED blob at reconcile, so
  // this is a convenience for the renderer and not a trusted input.
  const failedWorkloadKeys = new Set(
    asArray(results?.DataSourceErrors).map((d) => String(d?.workload || '')).filter(Boolean),
  );
  const attackerActions = buildAttackerActions({
    events,
    attackerCtx,
    attackerDeviceRows: attackerDeviceFacts,
    attackerApps,
    failedWorkloads: failedWorkloadKeys,
    phishingPages,
    noTokenIssued: !!attackerWindow?.noTokenIssued,
    requestedApps: attackerWindow?.attackerAccess?.requestedApps || [],
  });

  // ── Malicious IPs (the server rebuilds this from the case entity anyway,
  //    but the enrichment fields it keeps for agreed IPs come from here) ───
  // 🔴 The per-IP event count spans BOTH feeds. On the interactive stream alone, three of
  // this case's nine malicious addresses rendered "0 events" beside "Malicious" in the
  // client-facing table — 216.203.13.24, 216.203.13.26 and 43.166.208.95, the last of which
  // carries 113 non-interactive records and was classified malicious BECAUSE of them. An
  // address asserted malicious with zero displayed evidence reads as unsupported, and it
  // undercuts exactly the addresses the non-interactive panel exists to surface.
  //
  // Non-interactive rows are weighted by their own `EventCount`: the feed arrives
  // pre-aggregated at (session, IP, hour) grain and that field is Microsoft's own count of
  // authentication events in the bucket, so summing them gives events — not records.
  const niEventsByIp = new Map();
  const niFirstLast = new Map();
  // First/last seen span the whole run too, matching the counts below.
  for (const evt of asArray(results?.NonInteractiveSignInEvents || fr.NonInteractiveSignInEvents)) {
    const ip = evt?.IPAddress;
    if (!ip) continue;
    niEventsByIp.set(ip, (niEventsByIp.get(ip) || 0) + (Number(evt.EventCount) || 1));
    const start = evt.EventTimeUTC || '';
    const end = evt.LastEventTimeUTC || evt.EventTimeUTC || '';
    const cur = niFirstLast.get(ip) || { first: '', last: '' };
    if (start && (!cur.first || start < cur.first)) cur.first = start;
    if (end && end > cur.last) cur.last = end;
    niFirstLast.set(ip, cur);
  }

  // 🔴 COUNTED OVER THE WHOLE RUN, NOT THE CURATED VIEW (schema 1.23; pipeline review B2). The
  // report's canonical view hides failed events, so an address whose sign-ins were REFUSED
  // printed only its "successful" MFA-step rows -- 9 / 3 / 10 on IR-20261013-levig-001 -- and
  // dropped the Login:login rows and the blocked SAS:ProcessAuth, the most significant record
  // in the incident. A per-address evidence count must not depend on which pills were on.
  const rawByIp = new Map();
  for (const key of RESULT_ARRAYS) {
    if (key === 'NonInteractiveSignInEvents') continue;   // weighted by EventCount below
    for (const e of asArray(results?.[key] || fr[key])) {
      const a = e?.IPAddress || e?.InferredIP;
      if (!a) continue;
      if (!rawByIp.has(a)) rawByIp.set(a, []);
      rawByIp.get(a).push(e);
    }
  }
  const rawNiByIp = new Map();
  for (const evt of asArray(results?.NonInteractiveSignInEvents || fr.NonInteractiveSignInEvents)) {
    const a = evt?.IPAddress;
    if (a) rawNiByIp.set(a, (rawNiByIp.get(a) || 0) + (Number(evt.EventCount) || 1));
  }

  const maliciousIpRows = (attackerWindow?.maliciousIPs || []).map((ip) => {
    const enrich = ipLookup[ip] || {};
    const ipEvents = (rawByIp.get(ip) || [])
      .filter((e) => e.EventTimeUTC)
      .sort((a, b) => String(a.EventTimeUTC).localeCompare(String(b.EventTimeUTC)));
    const failedSignIns = (asArray(results?.SignInEvents || fr.SignInEvents))
      .filter((e) => e.IPAddress === ip && String(e.ResultStatus || '') !== 'Success').length;
    // Sign-in ATTEMPTS from the address (schema 1.25): distinct transactions, the same key
    // attackerAccess.js counts by. "14 failed sign-ins" beside "all 6 attempts were refused"
    // read as two different counts of one thing, and left the other rows to be read as
    // successes when they were the MFA-wait steps of those same attempts.
    const attemptKeys = new Set();
    for (const e of asArray(results?.SignInEvents || fr.SignInEvents)) {
      if (e.IPAddress !== ip) continue;
      const c = String(e.CorrelationId || '').trim().toLowerCase();
      attemptKeys.add(c && c !== 'ff000000-0000-0000-0000-000000000000' ? `c:${c}` : `r:${e.EventId || attemptKeys.size}`);
    }
    const ni = niFirstLast.get(ip) || { first: '', last: '' };
    // First/last seen must span both feeds too, or an address known only from the
    // non-interactive feed shows a blank window beside a non-zero event count.
    const firstUtc = [ipEvents[0]?.EventTimeUTC, ni.first].filter(Boolean).sort()[0] || '';
    const lastUtc = [ipEvents[ipEvents.length - 1]?.EventTimeUTC, ni.last].filter(Boolean).sort().pop() || '';
    const first = firstUtc ? { EventTimeUTC: firstUtc } : undefined;
    const last = lastUtc ? { EventTimeUTC: lastUtc } : undefined;
    return {
      ip,
      city: enrich.City || enrich.city || '',
      state: enrich.State || enrich.state || '',
      country: enrich.Country || enrich.country || '',
      provider: enrich.Provider || enrich.provider || '',
      networkType: enrich.NetworkType || '',
      riskScore: enrich.RiskScore ?? '',
      isVpn: !!enrich.IsVPN,
      isProxy: !!enrich.IsProxy,
      eventCount: String(ipEvents.length + (rawNiByIp.get(ip) || 0)),
      // Of those, interactive sign-ins that FAILED or were refused (schema 1.23). Stated so a
      // reader can tell an address that got in from one that was turned away.
      failedSignInCount: String(failedSignIns),
      signInAttemptCount: String(attemptKeys.size),
      // 🔴 THE TOKEN PORTION, STATED (schema 1.15). `eventCount` above is a SUM of two
      // different things: recorded events from the audited feeds, plus authentications
      // counted inside pre-aggregated non-interactive buckets. On the reference case the
      // attacker address printed **72** with nothing saying it was 26 recorded events and
      // 46 authentications from a single 13-second bucket -- so a reader could not tell
      // sustained hands-on activity from one burst of token reuse, which is the difference
      // the whole non-interactive feed exists to surface.
      //
      // The total stays as it was (it is the honest headline: both halves ARE access
      // events); this names the half a reader cannot otherwise see. Rendered by
      // Format-ExecReportMaliciousIpTable as its own column.
      tokenEventCount: String(rawNiByIp.get(ip) || 0),
      signInCount: String((asArray(results?.SignInEvents || fr.SignInEvents)).filter((e) => e.IPAddress === ip).length),
      firstSeenDisplay: formatFactTime(first?.EventTimeUTC, tzIana),
      lastSeenDisplay: formatFactTime(last?.EventTimeUTC, tzIana),
      comment: ipComments[ip] || '',
    };
  });
  // Ordered by SUCCESSFUL activity (events minus failed sign-ins), most first, classification
  // order breaking ties -- so if the cap ever binds, it drops the quietest addresses, and the
  // table leads with the ones that matter. The server keeps this order (reconcile).
  const activity = (r) => (Number(r.eventCount) || 0) - (Number(r.failedSignInCount) || 0);
  const maliciousIps = maliciousIpRows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => activity(b.r) - activity(a.r) || a.i - b.i)
    .map(({ r }) => r)
    .slice(0, CAPS.maliciousIps);

  const aw = attackerWindow || {};
  const attackerWindowFacts = {
    // Attempt span when no token was issued (schema 1.25) -- see attemptWindowSpan.
    firstActivityDisplay: formatFactTime(attemptSpan.first, tzIana),
    // The same instant as a bare UTC calendar day, so the server can ask ONE question the
    // display string cannot answer: does the earliest activity we found sit against the edge
    // of the period we reviewed? If it does, the duration is a floor rather than a finding.
    // Never parse `firstActivityDisplay` for this -- it is localized prose.
    firstActivityDay: attemptSpan.first
      ? new Date(normalizeIso(attemptSpan.first)).toISOString().slice(0, 10)
      : '',
    lastActivityDisplay: formatFactTime(attemptSpan.last, tzIana),
    firstAttackerIP: aw.firstAttackerIP || '',
    lastAttackerIP: aw.lastAttackerIP || '',
    durationDisplay: windowDuration,
    cutoffMethod: aw.cutoffMethod || '',
    cutoffLabel: aw.cutoffLabel || '',
    cutoffDisplay: formatFactTime(aw.globalCutoff, tzIana),
    // The derivation, as data rather than prose: what the cutoff is anchored to, when that
    // happened, and how long an already-issued token may outlive it.
    cutoffAnchor: aw.cutoffAnchor || '',
    cutoffAnchorDisplay: aw.cutoffAnchorAt ? formatFactTime(aw.cutoffAnchorAt, tzIana) : '',
    cutoffAllowanceMinutes: Number(aw.cutoffAllowanceMinutes ?? aw.tokenLifetimeUsed ?? 0) || 0,
    tokenLifetimeUsed: aw.tokenLifetimeUsed ?? '',
    successfulAttackerSignInCount: aw.successfulAttackerSignInCount ?? 0,
    attackerSignInCount: aw.attackerSignInCount ?? 0,
    isIndeterminate: !!aw.isIndeterminate,
    // 🔴 WHAT THE ATTEMPTS ACHIEVED (schema 1.23; attackerAccess.js). `noTokenIssued` is true
    // when every attacker sign-in attempt was refused and nothing else was done from the
    // attacker's addresses. The renderer then says "sign-in attempts", not "active", and states
    // that no token was issued instead of a token lifetime. The server re-checks it against the
    // pinned blob and forces it false if any attacker address issued a token.
    noTokenIssued: !!aw.noTokenIssued,
    attemptCount: Number(aw.attackerAccess?.attemptCount) || 0,
    // How far the attempts got (schema 1.25; pipeline review of IR-20261021-ehribik-001). A
    // password accepted and an MFA challenge that was never approved is a different case from
    // a compromise, and the title, the exposure sentence and the bottom line say so. All four
    // are re-derived from the pinned blob at reconcile and only ever weakened.
    passwordAccepted: !!aw.attackerAccess?.passwordAccepted,
    mfaChallenged: !!aw.attackerAccess?.mfaChallenged,
    mfaChallengePush: !!aw.attackerAccess?.mfaChallengePush,
    mfaCompleted: !!aw.attackerAccess?.mfaCompleted,
    requestedApps: (aw.attackerAccess?.requestedApps || []).slice(0, 10),
    // The account's enabled/disabled history and its state at the END of the records
    // (pipeline review A2): a disable undone by directory sync must not read as "contained".
    accountState: {
      finalState: aw.accountState?.finalState || '',
      lastChangeDisplay: aw.accountState?.lastChangeAt ? formatFactTime(aw.accountState.lastChangeAt, tzIana) : '',
      lastChangeBy: aw.accountState?.lastChangeBy || '',
      reenabled: !!aw.accountState?.reenabled,
      viaDirectorySync: !!aw.accountState?.viaDirectorySync,
      changes: (aw.accountState?.changes || []).slice(0, 20).map((c) => ({
        type: c.type,
        dayDisplay: formatFactTime(c.time, tzIana, { dayNoYear: true }),
        timeDisplay: formatFactTime(c.time, tzIana, { timeOnly: true }),
        by: c.actor || '',
      })),
    },
    // Remediation-separated episodes (schema 1.19). A case with multiple compromises weeks
    // apart, each remediated, must not read as one continuous window. Display strings only —
    // the model never does timezone math; the server renders them. Under attacker_window.*,
    // which is a structural prefix, so redaction leaves them alone.
    multiEpisode: !!aw.multiEpisode,
    episodeCount: aw.episodeCount ?? 0,
    episodes: (aw.episodes || []).map((ep) => ({
      startDisplay: formatFactTime(ep.start, tzIana),
      contained: !!ep.contained,
      containedDisplay: ep.containedAt ? formatFactTime(ep.containedAt, tzIana) : '',
      containmentType: ep.containmentType || '',
      durationDisplay: ep.containedAt ? formatDurationText(ep.start, ep.containedAt) : '',
    })),
    // Containment. These drive the severity rubric server-side, so they are raw
    // ISO as well as display: the rubric only asks "is this set?".
    accountDisabledAt: aw.accountDisabledAt || '',
    accountDisabledDisplay: formatFactTime(aw.accountDisabledAt, tzIana),
    accountDisabledBy: aw.accountDisabledBy || '',
    tokensRevokedAt: aw.tokensRevokedAt || '',
    tokensRevokedDisplay: formatFactTime(aw.tokensRevokedAt, tzIana),
    tokensRevokedBy: aw.tokensRevokedBy || '',
    passwordResetAt: aw.passwordResetAt || '',
    passwordResetDisplay: formatFactTime(aw.passwordResetAt, tzIana),
    passwordResetBy: aw.passwordResetBy || '',
    // ISO, compared by the server against each mfa_changes entry's server-stamped eventUtc
    // (schema 1.27): a change made before the attack is not listed as mitigation.
    attackStartedAt: aw.attackStartedAt || '',
    // 🔴 EVERY containment occurrence, not just the earliest of each type (schema 1.20). The
    // three *Display fields above are the FIRST occurrence of each action — a single-incident
    // shape that, on a remediation-separated case, hid episode 2's own containment (Sep 11
    // disable + reset) from the Brief's "when it was contained" column. The renderer lists all
    // occurrences per action from this array. Display strings only (the model never does tz
    // math); under attacker_window.*, a structural prefix, so redaction leaves them alone.
    containmentEvents: (aw.containmentEvents || []).map((c) => ({
      type: c.type || '',
      display: formatFactTime(c.time, tzIana),
      // Split for the Brief's day-grouped containment column (schema 1.21): the server groups
      // occurrences by `dayDisplay` and lists each day's `timeDisplay`s on one line, so five
      // token-revoke instants no longer blend into one semicolon-joined blob that reads as a
      // single answer. Computed here, never parsed from `display` server-side.
      dayDisplay: formatFactTime(c.time, tzIana, { dayNoYear: true }),
      timeDisplay: formatFactTime(c.time, tzIana, { timeOnly: true }),
      by: c.actor || '',
    })),
  };

  const scopeNotes = [];
  // Two addresses for one mailbox reads as two people unless it is said plainly, and
  // the model has no other way to know. scope_notes is model context only — it is not
  // rendered — so the READER-facing version belongs with the item 5c disclosure block.
  if (mailboxAddresses.size > 1) {
    scopeNotes.push(
      `This mailbox sends and receives under more than one address (${[...mailboxAddresses].join(', ')}). `
      + 'They are the same person and the same mailbox; activity under all of them is included.',
    );
  }
  // "By the reviewing analyst", NOT "by the analyst's filters": crediting filters implies
  // a weaker, mechanical reason, when the exclusion was a deliberate professional
  // judgment. The READER-facing version of this is server-rendered into the report's
  // Scope section — scope_notes reaches the model only.
  // 🔴 THIS SENTENCE HAS BEEN WRONG TWICE, IN OPPOSITE DIRECTIONS. It first said the figures
  // came from incident-related events "not the raw log volume", which read as though the raw
  // volume was never examined; that was corrected (2026-08-21) to say the tenant's FULL
  // recorded log volume was reviewed -- which is FALSE, and is the sentence that licensed the
  // file finding (pipeline team §4.2). It now says "available", which is true of both.
  //
  // ⚠️ KEEP THIS IN STEP WITH Format-ExecReportExclusionNotice, which renders the
  // reader-facing version. scope_notes reaches the MODEL ONLY -- and the model echoing this
  // wording into client prose is exactly how the false claim reached the page.
  if (excludedEventCount > 0) {
    scopeNotes.push(
      'The investigation reviewed the log volume available for this tenant in this period and identified the '
      + 'events related to this incident: '
      + `${reviewedEventCount} events were incident related; `
      + `${excludedEventCount} were normal activity, set aside by the reviewing analyst. The figures in this `
      + 'report describe the incident-related events drawn from that volume.',
    );
  }
  // The roll-up disclosure. Records and rows are different numbers and a reader who counts
  // the timeline will find the smaller one, so say which is which rather than leaving the
  // difference to be discovered. Rendered for the reader by
  // Format-ExecReportExclusionNotice -- KEEP THE TWO IN STEP.
  if (reviewedRowCount > 0 && reviewedRowCount < reviewedEventCount) {
    scopeNotes.push(
      `Those ${reviewedEventCount} records are shown as ${reviewedRowCount} timeline entries: related records are `
      + 'grouped where they describe one thing, so a run of sign-ins from the same address becomes a single '
      + 'access period and the several records Microsoft writes for one administrative action become a single '
      + 'entry. Report the record counts, not the number of entries.',
    );
  }
  if (caseData?.ualReviewed) {
    scopeNotes.push('The reviewing analyst has recorded that records for this case were also retrieved directly from '
      + "Microsoft's Unified Audit Log, outside the monitored log feeds. The report states this in its scope notice; "
      + 'do not describe the review as limited to the monitored feeds.');
  }
  if (aw.isIndeterminate) scopeNotes.push('The end of the attacker window could not be determined from the logs.');
  if (maskSubjects) scopeNotes.push('Email subject lines were withheld from this report by policy.');
  if (!messageTrace.length) scopeNotes.push('No message-trace data was available for the reviewed period.');
  // 🔴 "was recorded" ASSERTED A NEGATIVE ABOUT A FEED THAT MAY NEVER HAVE BEEN COLLECTED --
  // the same defect v1.164.0 fixed on the checklist, still live here where the model can echo
  // it into client prose. On IR-20260818-Controller-001 SharePoint collection begins two days
  // AFTER containment, so "no activity was recorded" would have been true and deeply
  // misleading. It now describes the REVIEWED DATA, which is all this note can support.
  if (!sharePoint.length) scopeNotes.push('The reviewed records contain no SharePoint or OneDrive activity for this account. Whether those records span the whole period is stated per finding, not here.');

  // 🔴 APPLICATION identities only, and ATTACKER-attributed only. Two separate defects
  // lived here.
  //
  // (a) It was built from `_appName`, which falls back to `Operation` for every
  // non-sign-in row - correct for the timeline column, wrong here. The model was handed:
  // Update, Create, Update PasswordProfile, Add user, Invite external user, SendMail,
  // New-InboxRule, Update StsRefreshTokenValidFrom Timestamp, Reset user password,
  // Change user password, Admin deleted security info, User registered security info...
  // Twenty of twenty-four entries were OPERATIONS, six of them the DEFENDERS' remediation.
  // Under a heading reading "Apps & access paths - what touched the account" the model
  // duly wrote "The attacker also triggered directory-level operations related to password
  // resets and security info changes" into a client-facing report, attributing containment
  // to the attacker and contradicting the report's own Containment section.
  //
  // (b) Even cleaned of operations it listed every application in the curated view - 58 on
  // the reference run, most of them the real user's laptop and phone - while the section
  // reads as the attacker's. That is the same attribution hazard in different clothes, and
  // it left the list disagreeing with the `distinct_apps_used` stat printed beside it.
  //
  // So the array IS the metric's set: one definition, no gap for a narrator to fall into.
  const appAttr = buildAppAttribution({
    signInEvents: signIns,
    nonInteractiveEvents: asArray(fr.NonInteractiveSignInEvents),
    maliciousSet,
  });
  const apps = [...attackerApps]
    .sort((a, b) => a.localeCompare(b))
    .slice(0, CAPS.apps)
    .map((name) => {
      const row = {
        name,
        isKnown: (knownApps || []).some((k) => k.appName === name),
        attested: appAttr.attested.has(name),
      };
      const g = appAttr.groupOf.get(name);
      if (g) row.group = g;
      return row;
    });

  const startDisplay = formatFactTime(caseData?.startDate, tzIana, { dateOnly: true });
  const endDisplay = formatFactTime(caseData?.endDate, tzIana, { dateOnly: true });

  const pack = {
    fact_schema_version: FACT_SCHEMA_VERSION,
    // One aggregated record named more applications than it listed, so `apps` may be short.
    // A silent cap reads as "this is everything" -- it is not (schema 1.11).
    apps_list_truncated: appAttr.truncated,
    // 🔴 SENT FOR COMPLETENESS AND THEN OVERWRITTEN (schema 1.12). The operator's attestation
    // that records were retrieved directly from Microsoft's Unified Audit Log licenses a
    // plain negative on a feed whose warehouse records do not span the period -- the
    // strongest thing a checklist line says -- so `Invoke-ExecReportReconcile` re-reads it
    // from the Cases entity and discards this value, exactly as it does the malicious-IP set.
    // Only a global admin can set it; the report discloses it in the scope notice.
    ual_reviewed: Boolean(caseData?.ualReviewed),
    // 🔴 CARRIED FOR COMPLETENESS AND THEN OVERWRITTEN by the server from the Cases entity
    // (schema 1.18). It licenses the report to say the credentials were captured but no
    // attacker access was found — the most reassuring claim it makes — so it must not be
    // client-suppliable; Invoke-ExecReportReconcile rebuilds it and forces it back to
    // 'access_confirmed' if any IP is malicious (malicious wins).
    compromise_scope: caseData?.compromiseScope === 'credentials_captured_no_access_found'
      ? 'credentials_captured_no_access_found'
      : 'access_confirmed',
    app_version: appVersion,
    case_meta: {
      caseId: caseData?.caseId || '',
      mailbox: caseData?.userPrincipalName || '',
      orgName: caseData?.tenantName || '',
      startDate: caseData?.startDate || '',
      endDate: caseData?.endDate || '',
      iocDate: caseData?.iocDate || '',
      ticketNumber: caseData?.ticketNumber || '',
      accessWindowDisplay: attackerWindowFacts.firstActivityDisplay
        ? `${attackerWindowFacts.firstActivityDisplay}${windowDuration ? ` (${windowDuration})` : ''}`
        : 'Not determined',
      logsReviewedDisplay: startDisplay && endDisplay ? `${startDisplay} – ${endDisplay}` : '',
      generatedAtDisplay: formatFactTime(new Date().toISOString(), tzIana, { dateOnly: true }),
      timelineCaption: startDisplay && endDisplay ? `${startDisplay} – ${endDisplay} · ${tzLabel || 'UTC'}` : (tzLabel || 'UTC'),
    },
    filter_snapshot: {
      includeLogBreakdown: !!includeLogBreakdown,
      hideMsSubstrate: !!hideMsSubstrate,
      hideOutlookNoise: !!hideOutlookNoise,
      maskSubjects: !!maskSubjects,
      selectedTz: selectedTz || '',
      tzLabel: tzLabel || 'UTC',
      safeIpCount: effectiveSafeIPSet ? effectiveSafeIPSet.size : 0,
      tenantSafeIpCount: (tenantSafeIPs || []).length,
      // CaseDetail holds this as a Set; a caller (or a test) may pass an array.
      dismissedEventCount: dismissedEventIds instanceof Set ? dismissedEventIds.size : (dismissedEventIds || []).length,
      // The profile NAME, so a future change to the required set stays distinguishable
      // without a reader having to compare fourteen booleans.
      reportFilterProfile: REPORT_FILTER_PROFILE,
      // ...and the states themselves, because the server verifies them rather than
      // trusting the stamp.
      ...filterSnapshotStates(filterState, { hideMsSubstrate, hideOutlookNoise }),
      excludedEventCount: Number(excludedEventCount) || 0,
      reviewedEventCount: Number(reviewedEventCount) || 0,
      // Rows the timeline draws for those records (schema 1.13). Carried so the renderer can
      // state both numbers instead of leaving a reader to find the difference by counting.
      reviewedRowCount: Number(reviewedRowCount) || 0,
      runId,
    },
    date_range: { from: caseData?.startDate || '', to: caseData?.endDate || '' },
    attacker_window: attackerWindowFacts,
    malicious_ips: maliciousIps,
    metrics,
    timeline,
    mailbox_folders: mailbox.folders.slice(0, CAPS.folders),
    accessed_items: accessed.items,
    inbox_rules: inboxRules,
    mfa_changes: mfaChanges,
    // The Executive BRIEF's page-1 checklist. ALWAYS present (unlike attacker_devices and
    // phishing_entry_point, whose absence is itself a fact): a checklist with a line
    // missing is not a shorter checklist, it is an unanswered question, and the whole
    // point of the three-state design is that "we could not tell" is stated rather than
    // omitted. Renderer-only — stripped before the provider call.
    attacker_actions: attackerActions,
    // What remains exposed (schema 1.23; attackerAccess.buildExposures). Each entry is present
    // only when its condition holds; the SERVER verifies each against the pinned blob and
    // renders the wording. Always an array, so "nothing to report" is a stated empty list.
    exposures: buildExposures({
      attackerWindow,
      messageTraceEvents: asArray(results?.MessageTraceEvents || fr.MessageTraceEvents),
      phishingFlags: phishFlags,
      mailboxAddresses,
    }).map((x) => ({
      id: x.id,
      ...(x.at ? { timeDisplay: formatFactTime(x.at, tzIana) } : {}),
      ...(x.ip ? { ip: x.ip } : {}),
      ...(x.id === 'mfa_approved' ? { push: !!x.push } : {}),
      ...(x.id === 'password_known' ? { directorySynced: !!x.directorySynced } : {}),
      ...(x.sender ? { sender: x.sender, domain: x.domain, priorMessages: String(x.priorMessages) } : {}),
    })),
    phishing_pages: phishingPages.slice(0, 5),
    // Present only when the case HAS attacker devices, so the section's absence is a fact
    // about the case rather than a rendering quirk (same rule as phishing_entry_point).
    ...(attackerDeviceFacts.length ? { attacker_devices: attackerDeviceFacts } : {}),
    apps,
    // The analyst's entry point. Replaces `url_clicks_confirmed`, which carried ONLY an
    // eventId — no URL, time, user or IP — so nothing could be written or rendered about
    // it and it fed only the `confirmed_phish_clicks` count. Present only when a marker
    // exists, so the section's absence is a fact about the case, not a rendering quirk.
    ...((phishEntry.emails.length || phishEntry.clicks.length)
      ? { phishing_entry_point: phishEntry }
      : {}),
    message_trace_counts: { total: messageTrace.length, outbound: outbound.length, inbound: messageTrace.length - outbound.length },
    scope_notes: scopeNotes.slice(0, CAPS.scopeNotes),
    log_types_reviewed: logTypesReviewed,
    timeline_digest: timelineDigest,
    // 🔴 SEEDED WITH THE TIMELINE'S OWN LOSS, not zero (v1.176.0). This was literally
    // `omitted_count: 0` set AFTER the timeline had already been sliced, so the one
    // collection whose truncation removed findings was the one collection that never
    // reported it. `enforceFactPackSize` adds its own drops to this figure.
    omitted_count: timelineOmitted,
    timeline_omitted: String(timelineOmitted),
    timeline_significant_total: String(significantRows.length),
    facts_truncated: timelineOmitted > 0,
  };

  return enforceFactPackSize(pack);
}

/**
 * Count the verbatim subjects a pack carries. The UI uses this to state the
 * consequence at the point of decision — an unmasked report is a DURABLE
 * subject-bearing artifact readable by anyone with case access, which the
 * ephemeral per-analyst reveal toggle is not.
 */
export function countPackSubjects(pack) {
  // Counts the item TEXT the pack carries, which is only ever present on an unmasked
  // run. Mirrors Get-ExecReportSubjectCount server-side — the two must agree, because
  // the server rejects a pack that claims masking while carrying item text.
  return (pack?.accessed_items || []).filter((r) => r?.item).length;
}

// maskSubject is imported so the module owns one masking decision point even
// though the pack currently omits subjects wholesale rather than masking them
// in place; re-exported for the tests to assert against.
export { maskSubject };
